// GS Mandal — Trust public website at "/"
// Distinct heritage palette: Royal Blue (#0D5C63) + Gold (#F26419) + Ivory cream
// 8 menu items: Home, Overview, Pioneers, Trustees, Institutes, Our Team, Gallery, Contact
import React, { useEffect, useState, useMemo, createContext, useContext } from "react";
import { NavLink, Outlet, Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "@/lib/api";
import {
  Phone, Mail, MapPin, Menu, X, ArrowRight, ArrowLeft, ChevronDown,
  GraduationCap, Building2, Users, Award, Newspaper, Image as ImageIcon,
  Facebook, Instagram, Linkedin, Youtube, ExternalLink, Calendar,
  Sparkles, Quote, Loader2, Briefcase, Upload, HeartHandshake, PlayCircle,
  Wrench, HeartPulse, Utensils, Atom, School as SchoolIcon, Baby, BookOpen,
  Monitor, Laptop, Stethoscope, FlaskConical, ChefHat, Presentation,
} from "lucide-react";
import { toast } from "sonner";

const FALLBACK_LOGO_URL = "https://customer-assets.emergentagent.com/job_trust-admin-hub-1/artifacts/4m1usgtq_logo.jpeg";
const Ctx = createContext(null);
export const useTrust = () => useContext(Ctx);

const NAV = [
  { to: "", end: true, label: "Home" },
  {
    to: "overview",
    label: "Our Journey",
    children: [
      { to: "overview", label: "Our Journey" },
      { to: "pioneers", label: "Founder" },
      { to: "trustees", label: "Trustees" },
    ],
  },
  { to: "institutes", label: "Institutes" },
  { to: "our-team", label: "Our Team" },
  {
    to: "gallery",
    label: "Gallery",
    children: [
      { to: "gallery", label: "Gallery" },
      { to: "memories", label: "Share Your Memories" },
    ],
  },
  { to: "careers", label: "Careers" },
  { to: "fees", label: "Know Your Fees" },
  { to: "social-impact", label: "Social Impact" },
  { to: "amenities", label: "Amenities" },
  {
    to: "tenders",
    label: "Tenders",
    children: [
      { to: "vendors/register", label: "Empanelment / Vendor Registration" },
      { to: "tenders", label: "View Tenders" },
    ],
  },
];

// Flat list for footer + mobile — expands "Our Journey" into its 3 sub-links so
// every destination remains reachable without extra UI.
const NAV_FLAT = NAV.flatMap((n) => (Array.isArray(n.children) && n.children.length ? n.children : [n]));

// Small hover/click dropdown for the Trust primary nav. Used when a NAV entry
// declares `children` — e.g. "Our Journey" now groups Our Journey/Founder/Trustees.
function TrustNavDropdown({ item, alignRight = false }) {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);
  const loc = useLocation();

  useEffect(() => { setOpen(false); }, [loc.pathname]);
  useEffect(() => {
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const childSlugs = item.children.map((c) => c.to);
  const activeSeg = loc.pathname.split("/").filter(Boolean)[0] || "";
  const isActive = childSlugs.includes(activeSeg);
  const triggerCls = `trust-nav-link trust-eyebrow whitespace-nowrap text-[11px] font-semibold px-3 py-4 transition-colors inline-flex items-center gap-1 ${isActive ? "active text-[#F26419]" : "text-[#3E3E3E] hover:text-[#0D5C63]"}`;

  return (
    <div ref={ref} className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)} data-testid={`trust-nav-${item.to}-root`}>
      <button type="button" onClick={() => setOpen((v) => !v)} className={triggerCls} data-testid={`trust-nav-${item.to}-trigger`} aria-haspopup="menu" aria-expanded={open}>
        {item.label}
        <ChevronDown size={12} strokeWidth={2.4} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div role="menu" className={`absolute ${alignRight ? "right-0" : "left-0"} top-full pt-1 min-w-[220px] z-50`} data-testid={`trust-nav-${item.to}-menu`}>
          <div className="rounded-sm shadow-lg py-1 bg-white ring-1 ring-[#E9E2D2]">
            {item.children.map((c) => (
              <NavLink
                key={c.to}
                to={c.to}
                end={c.to === ""}
                data-testid={`trust-nav-${item.to}-item-${c.to || "home"}`}
                className={({ isActive }) => `block px-4 py-2 text-sm font-medium ${isActive ? "text-[#F26419] font-semibold" : "text-[#3E3E3E] hover:bg-[#F5EFE3] hover:text-[#0D5C63]"}`}
              >
                {c.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function fmtDate(s) {
  if (!s) return "";
  try { return new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); } catch { return s; }
}

function renderHtml(t) {
  if (!t) return "";
  const isHtml = /<\/?[a-z][\s\S]*?>/i.test(t);
  return isHtml ? t : t.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br/>");
}

// Sort institutes chronologically (oldest → newest) by year_of_establishment.
// Institutes without a year fall back to the end, sub-ordered by name.
function byYear(list = []) {
  return [...list].sort((a, b) => {
    const ay = Number(a?.year_of_establishment) || 9999;
    const by = Number(b?.year_of_establishment) || 9999;
    if (ay !== by) return ay - by;
    return (a?.name || "").localeCompare(b?.name || "");
  });
}

// Style palette rotated through when rendering discipline categories.
// Falls back gracefully when an institute's `type` is not pre-styled.
const CATEGORY_STYLES = [
  { icon: "🎓", accent: "#0D5C63" },
  { icon: "⚙️", accent: "#F26419" },
  { icon: "🔧", accent: "#D90368" },
  { icon: "🩺", accent: "#7B4E96" },
  { icon: "🔬", accent: "#0EA5E9" },
  { icon: "🏫", accent: "#EAB308" },
  { icon: "📚", accent: "#14532D" },
  { icon: "💼", accent: "#B45309" },
];

// Deterministic style pick based on the type string — same type always gets the same colour.
function styleForType(type) {
  const key = String(type || "").trim();
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return CATEGORY_STYLES[hash % CATEGORY_STYLES.length];
}

// ============================================================
// LAYOUT
// ============================================================
export default function TrustLayout() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get("/public/trust");
        if (!cancel) setData(data);
      } catch (e) {
        if (!cancel) setErr(e?.response?.data?.detail || "Unable to load trust data");
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, []);

  useEffect(() => { window.scrollTo({ top: 0 }); setMobileOpen(false); }, [location.pathname]);

  if (loading) {
    return <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center" data-testid="trust-loading"><Loader2 size={32} className="animate-spin text-[#F26419]" /></div>;
  }
  if (err || !data) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center px-6 text-center">
        <div>
          <Building2 size={36} className="mx-auto text-[#0D5C63]" />
          <h1 className="mt-3 text-2xl font-serif text-[#1c1a17]">Website temporarily unavailable</h1>
          <p className="mt-2 text-sm text-[#5B5B5B]">{err || "Please try again later."}</p>
        </div>
      </div>
    );
  }

  const t = data.trust || {};
  const instName = t.trust_name || "Gramaudyogik Shikshan Mandal";
  // Contact details are picked up from the Registered Office tab of Trust Details.
  const corrEmail = t.reg_email || t.corr_email || "";
  const corrPhone = t.reg_contact || t.corr_contact || "";
  const corrAddr = [t.reg_address || t.corr_address, t.reg_city || t.corr_city, t.reg_state || t.corr_state, t.reg_pincode || t.corr_pincode].filter(Boolean).join(", ");
  const logo = t.logo_url || FALLBACK_LOGO_URL;

  return (
    <Ctx.Provider value={data}>
      <Helmet>
        <title>Gramaudyogik Shikshan Mandal (MIT Group of Institutions) | Education, Healthcare &amp; Skill Development Since 1976</title>
        <meta name="description" content="Gramaudyogik Shikshan Mandal (MIT Group of Institutions) is one of Maharashtra's leading educational trusts, offering Engineering, Polytechnic, Nursing, Food Technology, Emerging Science, Management, Healthcare, School Education and Skill Development since 1976." />
        <meta name="keywords" content="GS Mandal, Gramaudyogik Shikshan Mandal, MIT Group of Institutions, MIT Aurangabad, MIT Chhatrapati Sambhajinagar, Engineering College, Polytechnic, Nursing College, Food Technology, Emerging Science, MBA, MCA, BCA, School, Hospital, Education Trust Maharashtra, Technical Education, Healthcare, Skill Development" />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta name="author" content="Gramaudyogik Shikshan Mandal" />
        <link rel="canonical" href="https://gsmandal.org/" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Gramaudyogik Shikshan Mandal (MIT Group of Institutions)" />
        <meta property="og:description" content="Empowering generations through education, healthcare, research and skill development since 1976. Explore 13+ institutes under the MIT Group of Institutions." />
        <meta property="og:url" content="https://gsmandal.org/" />
        <meta property="og:site_name" content="GS Mandal" />
        <meta property="og:image" content="https://gsmandal.org/images/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_IN" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Gramaudyogik Shikshan Mandal (MIT Group of Institutions)" />
        <meta name="twitter:description" content="Leading educational and healthcare trust with Engineering, Polytechnic, Nursing, Hospital, Emerging Science, Food Technology and more." />
        <meta name="twitter:image" content="https://gsmandal.org/images/og-image.jpg" />
        <meta name="geo.region" content="IN-MH" />
        <meta name="geo.placename" content="Chhatrapati Sambhajinagar" />
        <meta name="ICBM" content="19.8762,75.3433" />
        <meta name="theme-color" content="#0A4D8C" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800;900&family=DM+Sans:wght@400;500;600;700&family=Eczar:wght@500;600;700;800&family=Tiro+Devanagari+Marathi&display=swap" />
        <style>{`
          .trust-serif{font-family:'Outfit','Tiro Devanagari Marathi',sans-serif;letter-spacing:-0.02em;font-weight:700;}
          .trust-sans{font-family:'DM Sans',sans-serif;}
          .trust-accent{font-family:'Eczar',serif;font-weight:600;}
          .trust-eyebrow{font-family:'DM Sans',sans-serif;text-transform:uppercase;letter-spacing:0.28em;font-weight:600;}
          .gold-rule{background:linear-gradient(90deg,transparent,#F26419 50%,transparent);height:2px;}
          .gold-rule-solid{background:linear-gradient(90deg,#F26419,#D90368);height:3px;width:56px;border-radius:2px;}
          .rani-underline{position:relative;}
          .rani-underline::after{content:"";position:absolute;left:0;bottom:-6px;height:4px;width:44px;background:#D90368;border-radius:2px;}
          .grad-text{background:linear-gradient(90deg,#F26419 0%,#D90368 100%);-webkit-background-clip:text;background-clip:text;color:transparent;}
          .grad-text-teal{background:linear-gradient(90deg,#0D5C63 0%,#0D5C63 60%,#F26419 100%);-webkit-background-clip:text;background-clip:text;color:transparent;}
          @keyframes trust-marq{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}
          @keyframes trust-float{0%,100%{transform:translate(0,0)}50%{transform:translate(4px,-6px)}}
          @keyframes trust-rise{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
          .trust-rise{animation:trust-rise 0.7s ease-out both;}
          .trust-rise-1{animation-delay:0.05s} .trust-rise-2{animation-delay:0.15s} .trust-rise-3{animation-delay:0.25s} .trust-rise-4{animation-delay:0.35s}
          .trust-grain{background-image:radial-gradient(circle at 22% 18%, rgba(13,92,99,0.06) 0, transparent 42%),radial-gradient(circle at 78% 82%, rgba(242,100,25,0.08) 0, transparent 48%),radial-gradient(circle at 50% 50%, rgba(217,3,104,0.03) 0, transparent 55%);}
          .rangoli-dot{position:absolute;width:6px;height:6px;border-radius:50%;background:currentColor;opacity:0.35;}
          .trust-card-hover{transition:transform 0.35s cubic-bezier(.2,.7,.2,1),box-shadow 0.35s;}
          .trust-card-hover:hover{transform:translateY(-4px);box-shadow:0 24px 48px -20px rgba(13,92,99,0.25);}
          .trust-nav-link{position:relative;}
          .trust-nav-link::after{content:"";position:absolute;left:12px;right:12px;bottom:10px;height:2px;background:linear-gradient(90deg,#F26419,#D90368);transform:scaleX(0);transform-origin:left;transition:transform 0.35s ease;}
          .trust-nav-link:hover::after,.trust-nav-link.active::after{transform:scaleX(1);}
        `}</style>
      </Helmet>

      <div className="min-h-screen flex flex-col bg-[#FAFAF7] text-[#1A1A1A] trust-sans">
        {/* Top utility — trust registration info */}
        <div className="bg-gradient-to-r from-[#0D5C63] via-[#083E43] to-[#0D5C63] text-white/90 text-[11px]">
          <div className="max-w-7xl mx-auto px-5 py-2 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 trust-sans" data-testid="trust-top-reg-info">
              {t.registration_number && (
                <span className="inline-flex items-center gap-1.5" data-testid="trust-top-reg-no">
                  <span className="trust-eyebrow text-[9px] text-[#F26419] font-bold">Reg. No.</span>
                  <span className="font-semibold">{t.registration_number}</span>
                </span>
              )}
              {t.registered_under_act && (
                <span className="inline-flex items-center gap-1.5" data-testid="trust-top-act">
                  <span className="trust-eyebrow text-[9px] text-[#F26419] font-bold">Registered Under</span>
                  <span>{t.registered_under_act}</span>
                </span>
              )}
              {t.registration_date && (
                <span className="inline-flex items-center gap-1.5" data-testid="trust-top-reg-date">
                  <span className="trust-eyebrow text-[9px] text-[#F26419] font-bold">Registration Date</span>
                  <span>{fmtDate(t.registration_date)}</span>
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              {corrEmail && <a href={`mailto:${corrEmail}`} className="inline-flex items-center gap-1.5 hover:text-[#F26419] transition-colors" data-testid="trust-top-email"><Mail size={11} />{corrEmail}</a>}
              {corrPhone && <a href={`tel:${corrPhone}`} className="hidden md:inline-flex items-center gap-1.5 hover:text-[#F26419] transition-colors" data-testid="trust-top-phone"><Phone size={11} />{corrPhone}</a>}
              <div className="flex items-center gap-2 border-l border-white/20 pl-3">
                <a href="#" className="hover:text-[#F26419] transition-colors" aria-label="Facebook"><Facebook size={12} /></a>
                <a href="#" className="hover:text-[#F26419] transition-colors" aria-label="Instagram"><Instagram size={12} /></a>
                <a href="#" className="hover:text-[#F26419] transition-colors" aria-label="LinkedIn"><Linkedin size={12} /></a>
                <a href="#" className="hover:text-[#F26419] transition-colors" aria-label="YouTube"><Youtube size={12} /></a>
              </div>
            </div>
          </div>
        </div>

        {/* Header band */}
        <header className="sticky top-0 z-40 bg-[#FAFAF7]/95 backdrop-blur-xl border-b border-[#E9E2D2] shadow-sm">
          <div className="max-w-7xl mx-auto px-5 py-4 flex items-center gap-5">
            <NavLink to="" end className="flex items-center gap-4 shrink-0" data-testid="trust-logo">
              <div className="relative">
                <img src={logo} alt={instName} className="h-[72px] w-[72px] object-contain relative z-10" />
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#F26419]/15 to-[#D90368]/10 blur-lg" />
              </div>
              <div className="hidden md:block">
                <div className="trust-eyebrow text-[10px] text-[#F26419] font-bold">Heritage · Knowledge · Impact</div>
                <div className="trust-serif text-2xl text-[#1A1A1A] leading-none mt-1" data-testid="trust-name">{instName}</div>
                <div className="text-[11px] text-[#5B5B5B] mt-0.5">A charitable educational trust · {data.institutes.length} institutes · 30,000+ alumni</div>
              </div>
            </NavLink>
            <div className="ml-auto hidden lg:flex items-center gap-2">
              <Link to="institutes" className="trust-eyebrow text-[10px] text-[#0D5C63] hover:text-[#F26419] px-3 py-2 font-semibold transition-colors">Our Institutes</Link>
              <Link to="contact" data-testid="trust-cta-contact" className="group inline-flex items-center gap-2 bg-gradient-to-r from-[#F26419] to-[#D90368] hover:from-[#D14E10] hover:to-[#B00252] text-white text-xs font-bold uppercase tracking-wider px-5 py-2.5 rounded-full shadow-md shadow-[#F26419]/25 hover:shadow-lg transition-all hover:-translate-y-0.5">
                Get in Touch <ArrowRight size={13} className="group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>
            <button onClick={() => setMobileOpen((v) => !v)} className="ml-auto xl:hidden p-2 text-[#0D5C63]" aria-label="Menu" data-testid="trust-nav-toggle">{mobileOpen ? <X /> : <Menu />}</button>
          </div>

          {/* Primary nav */}
          <nav className="border-t border-[#E9E2D2] bg-white/60 backdrop-blur" data-testid="trust-nav">
            <div className="max-w-7xl mx-auto px-5">
              <div className="hidden xl:flex items-center justify-between gap-1">
                {NAV.map((n, idx) => (
                  Array.isArray(n.children) && n.children.length ? (
                    <TrustNavDropdown key={n.to} item={n} alignRight={idx >= NAV.length - 2} />
                  ) : (
                    <NavLink key={n.to} to={n.to} end={n.end} data-testid={`trust-nav-${n.to || "home"}`}
                      className={({ isActive }) => `trust-nav-link trust-eyebrow whitespace-nowrap text-[11px] font-semibold px-3 py-4 transition-colors ${isActive ? "active text-[#F26419]" : "text-[#3E3E3E] hover:text-[#0D5C63]"}`}>
                      {n.label}
                    </NavLink>
                  )
                ))}
              </div>
              {mobileOpen && (
                <div className="xl:hidden grid grid-cols-2 gap-1 py-3">
                  {NAV_FLAT.map((n) => (
                    <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setMobileOpen(false)}
                      className={({ isActive }) => `trust-eyebrow text-xs px-3 py-2.5 rounded-lg font-semibold ${isActive ? "bg-gradient-to-r from-[#F26419] to-[#D90368] text-white" : "text-[#3E3E3E] hover:bg-[#0D5C63]/5"}`}>
                      {n.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          </nav>
        </header>

        <main className="flex-1"><Outlet /></main>

        {/* Footer */}
        <footer className="relative bg-[#1A1A1A] text-white/85 mt-20">
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#0D5C63] via-[#F26419] to-[#D90368]" />
          <div className="max-w-7xl mx-auto px-5 py-16 grid md:grid-cols-4 gap-10">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3">
                <img src={logo} alt={instName} className="h-14 w-14 object-contain bg-white p-1 rounded-xl" />
                <div>
                  <div className="trust-serif text-2xl text-white leading-none">{instName}</div>
                  <div className="trust-eyebrow text-[10px] text-[#F26419] mt-1">Heritage · Knowledge · Impact</div>
                </div>
              </div>
              <p className="mt-5 text-sm leading-relaxed max-w-md text-white/70">A registered charitable educational trust nurturing technical, professional and pre-vocational excellence across {data.institutes.length} institutes. Empowering generations since {t.registration_date ? new Date(t.registration_date).getFullYear() : "decades"}.</p>
              <div className="mt-5 flex items-center gap-3">
                {[Facebook, Instagram, Linkedin, Youtube].map((Icon, i) => (
                  <a key={i} href="#" className="w-9 h-9 rounded-full border border-white/20 hover:border-[#F26419] hover:bg-[#F26419]/10 hover:text-[#F26419] flex items-center justify-center transition-all hover:-translate-y-0.5" aria-label="social"><Icon size={13} /></a>
                ))}
              </div>
            </div>
            <div>
              <div className="trust-eyebrow text-[10px] text-[#F26419] font-bold mb-4">Explore</div>
              <ul className="space-y-2.5 text-sm">
                {NAV_FLAT.map((n) => <li key={n.to}><NavLink to={n.to} end={n.end} className="hover:text-[#F26419] transition-colors">{n.label}</NavLink></li>)}
              </ul>
            </div>
            <div>
              <div className="trust-eyebrow text-[10px] text-[#F26419] font-bold mb-4">Contact</div>
              <ul className="space-y-3 text-sm">
                {corrAddr && <li className="flex items-start gap-2"><MapPin size={13} className="mt-0.5 text-[#F26419] shrink-0" />{corrAddr}</li>}
                {corrEmail && <li className="flex items-start gap-2"><Mail size={13} className="mt-0.5 text-[#F26419]" /><a href={`mailto:${corrEmail}`} className="hover:text-[#F26419] break-all transition-colors">{corrEmail}</a></li>}
                {corrPhone && <li className="flex items-start gap-2"><Phone size={13} className="mt-0.5 text-[#F26419]" />{corrPhone}</li>}
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 py-4">
            <div className="max-w-7xl mx-auto px-5 flex flex-wrap items-center justify-between gap-3 text-[11px] text-white/55">
              <div>© {new Date().getFullYear()} {instName}. All rights reserved.</div>
              <div className="flex items-center gap-4">
                <NavLink to="memories" data-testid="trust-footer-memories" className="hover:text-white underline underline-offset-2">Share Your Memories</NavLink>
                <a href="/sitemap.xml" target="_blank" rel="noopener noreferrer" data-testid="trust-footer-sitemap" className="hover:text-white underline underline-offset-2">Sitemap</a>
                {t.registration_number && <span>Reg. No. {t.registration_number}</span>}
                {t.pan_number && <span>PAN {t.pan_number}</span>}
              </div>
            </div>
          </div>
        </footer>
      </div>
    </Ctx.Provider>
  );
}

// ============================================================
// HOME
// ============================================================
export function TrustHome() {
  const data = useTrust();
  const t = data.trust || {};
  const institutes = useMemo(() => byYear(data.institutes || []), [data.institutes]);
  const news = (data.news_events || []).filter((x) => x.item_type === "News").slice(0, 4);
  const events = (data.news_events || []).filter((x) => x.item_type === "Event").slice(0, 4);
  const importantLinks = data.important_links || [];
  // Prefer curated hero slides (uploaded from Admin CMS). Fallback to news/events cover images.
  const heroSlidesRaw = (data.hero_slides || []).filter((x) => x.image_url);
  const heroSlides = heroSlidesRaw.length > 0
    ? heroSlidesRaw.map((s) => ({ id: s.id, image_url: s.image_url, title: s.title || "", caption: s.caption || "" }))
    : (data.news_events || [])
        .filter((x) => x.cover_url)
        .slice(0, 5)
        .map((s) => ({ id: s.id, image_url: s.cover_url, title: s.title || "", caption: `${s.item_type || ""}${s.event_date ? " · " + fmtDate(s.event_date) : ""}` }));
  const [slide, setSlide] = useState(0);
  const total = heroSlides.length;

  useEffect(() => {
    if (total < 2) return;
    const id = setInterval(() => setSlide((s) => (s + 1) % total), 12000);
    return () => clearInterval(id);
  }, [total]);

  const yearsAgo = useMemo(() => {
    if (!t.registration_date) return null;
    const y = new Date(t.registration_date).getFullYear();
    if (!y) return null;
    return new Date().getFullYear() - y;
  }, [t]);

  const corrEmail = t.reg_email || t.corr_email || "";

  return (
    <div data-testid="trust-home">
      {/* HERO */}
      <section className="relative bg-[#FAFAF7] overflow-hidden trust-grain">
        {/* colorful decorative blobs */}
        <div className="absolute -top-24 -left-24 w-[380px] h-[380px] rounded-full bg-[#F26419]/12 blur-[80px] pointer-events-none" />
        <div className="absolute top-1/2 -right-32 w-[420px] h-[420px] rounded-full bg-[#0D5C63]/15 blur-[90px] pointer-events-none" />
        <div className="absolute bottom-10 left-1/3 w-[240px] h-[240px] rounded-full bg-[#D90368]/10 blur-[70px] pointer-events-none" />
        {/* rangoli dot cluster top-right */}
        <div className="absolute top-8 right-8 grid grid-cols-5 gap-2 opacity-40 pointer-events-none text-[#0D5C63]" aria-hidden>
          {Array.from({ length: 25 }).map((_, i) => <span key={i} className="rangoli-dot" style={{ position: "static" }} />)}
        </div>
        <div className="relative max-w-7xl mx-auto px-5 py-16 lg:py-24 grid lg:grid-cols-[1.2fr_1fr] gap-12 items-center">
          <div>
            <div className="trust-eyebrow text-[11px] text-[#D90368] font-bold inline-flex items-center gap-2 bg-[#D90368]/8 border border-[#D90368]/25 rounded-full px-3 py-1.5 trust-rise trust-rise-1">
              <Sparkles size={13} className="text-[#F26419]" /> Charitable Educational Trust {t.registration_number && `· ${t.registration_number}`}
            </div>
            <h1 className="trust-serif mt-6 text-5xl sm:text-6xl lg:text-[68px] text-[#1A1A1A] leading-[1.02] trust-rise trust-rise-2" data-testid="trust-hero-headline">
              <span className="grad-text-teal">{t.trust_name || "Gramaudyogik"}</span>{!t.trust_name && <> <span className="grad-text">Shikshan Mandal</span></>}
            </h1>
            <p className="mt-7 text-lg text-[#3E3E3E] max-w-xl leading-relaxed trust-rise trust-rise-3">
              Empowering generations through <span className="font-semibold text-[#0D5C63]">education</span>, <span className="font-semibold text-[#D90368]">healthcare</span>, <span className="font-semibold text-[#F26419]">skill development</span> and <span className="font-semibold text-[#0D5C63]">social impact</span>.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 trust-rise trust-rise-3">
              <Link to="institutes" className="group inline-flex items-center gap-2 bg-gradient-to-r from-[#F26419] to-[#D90368] hover:from-[#D14E10] hover:to-[#B00252] text-white font-semibold text-[13px] uppercase tracking-wider px-6 py-3 rounded-full shadow-lg shadow-[#F26419]/25 hover:shadow-xl hover:-translate-y-0.5 transition-all" data-testid="trust-hero-cta-institutes">
                <GraduationCap size={15} /> Explore Institutes
              </Link>
              <Link to="institutes" className="group inline-flex items-center gap-2 bg-[#0D5C63] hover:bg-[#083E43] text-white font-semibold text-[13px] uppercase tracking-wider px-6 py-3 rounded-full shadow-md hover:-translate-y-0.5 transition-all" data-testid="trust-hero-cta-admissions">
                <Newspaper size={14} /> Admissions
              </Link>
              <a href={corrEmail ? `mailto:${corrEmail}?subject=Alumni%20Network%20Enquiry` : "#"} className="group inline-flex items-center gap-2 bg-white border-2 border-[#0D5C63] text-[#0D5C63] hover:bg-[#0D5C63] hover:text-white font-semibold text-[13px] uppercase tracking-wider px-6 py-2.5 rounded-full hover:-translate-y-0.5 transition-all" data-testid="trust-hero-cta-alumni">
                <Users size={14} /> Alumni
              </a>
              <a href={corrEmail ? `mailto:${corrEmail}?subject=Donation%20%2F%20CSR%20Partnership` : "#"} className="group inline-flex items-center gap-2 bg-white border-2 border-[#D90368] text-[#D90368] hover:bg-[#D90368] hover:text-white font-semibold text-[13px] uppercase tracking-wider px-6 py-2.5 rounded-full hover:-translate-y-0.5 transition-all" data-testid="trust-hero-cta-donate">
                <Award size={14} /> Donate / CSR
              </a>
              <Link to="contact" className="group inline-flex items-center gap-2 bg-white border-2 border-[#F26419] text-[#F26419] hover:bg-[#F26419] hover:text-white font-semibold text-[13px] uppercase tracking-wider px-6 py-2.5 rounded-full hover:-translate-y-0.5 transition-all" data-testid="trust-hero-cta-contact">
                <Phone size={14} /> Contact Trust
              </Link>
            </div>
            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-3 trust-rise trust-rise-4">
              <Stat n={yearsAgo ? `${yearsAgo}+` : "50+"} l="Years Legacy" color="#F26419" />
              <Stat n={`${institutes.length}+`} l="Institutes" color="#0D5C63" />
              <Stat n="30,000+" l="Alumni" color="#D90368" />
              <div className="bg-gradient-to-br from-[#0D5C63] to-[#083E43] text-white rounded-2xl px-4 py-3 shadow-md" data-testid="trust-hero-stat-ecosystem">
                <div className="trust-accent text-base leading-tight">Education → Healthcare</div>
                <div className="trust-eyebrow text-[9px] text-[#F26419] font-bold mt-1.5">Complete Ecosystem</div>
              </div>
            </div>
          </div>
          <div className="relative trust-rise trust-rise-2">
            <div className="aspect-[4/5] bg-gradient-to-br from-[#0D5C63] to-[#083E43] relative overflow-hidden shadow-2xl rounded-3xl ring-4 ring-white" data-testid="trust-hero-slider">
              {heroSlides.length > 0 ? (
                heroSlides.map((s, i) => (
                  <div key={s.id} className="absolute inset-0 transition-opacity duration-1000" style={{ opacity: i === slide ? 1 : 0 }} data-testid={`trust-hero-slide-${i}`}>
                    <img src={s.image_url} alt={s.title || "slide"} className="w-full h-full object-cover object-center" />
                    {(s.title || s.caption) && (
                      <>
                        <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-[#0D5C63] via-[#0D5C63]/70 to-transparent pointer-events-none" />
                        <div className="absolute bottom-6 left-6 right-6 text-white backdrop-blur-sm bg-black/10 rounded-2xl px-4 py-3">
                          {s.caption && <div className="trust-eyebrow text-[10px] text-[#F26419]">{s.caption}</div>}
                          {s.title && <div className="trust-serif text-2xl mt-1 leading-tight">{s.title}</div>}
                        </div>
                      </>
                    )}
                  </div>
                ))
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center text-white/85">
                    <img src={t.logo_url || FALLBACK_LOGO_URL} alt="" className="w-32 h-32 mx-auto mb-4 opacity-50" />
                    <div className="trust-accent text-4xl text-[#F26419]">Since 1965</div>
                    <div className="trust-eyebrow text-[10px] text-white/70 mt-2">A legacy of service</div>
                  </div>
                </div>
              )}
              {total > 1 && (
                <div className="absolute bottom-6 right-6 flex items-center gap-1.5" data-testid="trust-hero-dots">
                  {heroSlides.map((_, i) => (
                    <button key={i} onClick={() => setSlide(i)} aria-label={`slide ${i + 1}`} className={`h-1.5 rounded-full transition-all ${i === slide ? "w-8 bg-[#F26419]" : "w-3 bg-white/50 hover:bg-white/80"}`} data-testid={`trust-hero-dot-${i}`} />
                  ))}
                </div>
              )}
            </div>
            {/* Colorful frame accents */}
            <div className="absolute -top-5 -left-5 w-20 h-20 border-4 border-[#F26419] rounded-2xl -z-10" />
            <div className="absolute -bottom-5 -right-5 w-24 h-24 bg-[#D90368]/25 rounded-2xl -z-10" />
            <div className="absolute top-1/2 -left-4 w-3 h-16 bg-[#0D5C63] rounded-full -z-10" />
          </div>
        </div>
      </section>

      {/* SCROLLER */}
      {news.length > 0 && (
        <div className="bg-gradient-to-r from-[#F26419] via-[#D90368] to-[#F26419] text-white overflow-hidden shadow-inner" data-testid="trust-news-scroller">
          <div className="max-w-7xl mx-auto px-5 py-3 flex items-center gap-3">
            <span className="trust-eyebrow text-[10px] bg-white text-[#D90368] px-3 py-1 rounded-full font-black shrink-0 shadow-sm">Latest News</span>
            <div className="overflow-hidden flex-1">
              <div className="inline-block whitespace-nowrap text-sm font-medium" style={{ animation: "trust-marq 60s linear infinite" }}>
                {[...news, ...news].map((n, i) => (
                  <span key={i} className="inline-block px-4">
                    <span className="text-white mr-2">◆</span>{n.title} <span className="text-white/70 ml-1">({fmtDate(n.event_date)})</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* NEWS + EVENTS GRID */}
      {(news.length > 0 || events.length > 0) && (
        <section className="py-20 bg-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-[0.5]" style={{ backgroundImage: "radial-gradient(circle at 10% 20%, rgba(242,100,25,0.05) 0, transparent 40%), radial-gradient(circle at 90% 80%, rgba(13,92,99,0.05) 0, transparent 40%)" }} />
          <div className="relative max-w-7xl mx-auto px-5">
            <div className="text-center">
              <div className="trust-eyebrow text-[10px] text-[#D90368] font-bold">Latest from GS Mandal</div>
              <h2 className="trust-serif text-4xl sm:text-5xl text-[#1A1A1A] mt-2">News <span className="grad-text">&amp;</span> Events</h2>
              <div className="gold-rule-solid mx-auto mt-3" />
            </div>
            <div className="grid md:grid-cols-2 gap-10 mt-14">
              <NewsList title="News" items={news} accent="teal" testid="trust-news-list" />
              <NewsList title="Events" items={events} accent="saffron" testid="trust-events-list" />
            </div>
          </div>
        </section>
      )}

      <HomeGalleryStrip photos={data.gallery || []} />

      <ImportantLinksSection links={importantLinks} />

      {/* GS MANDAL JOURNEY TREE — moved to Home from Institutes page */}
      <JourneyTree institutes={institutes} />
    </div>
  );
}

function HomeGalleryStrip({ photos }) {
  // Shuffle once per fetch of the gallery; keeps the picks stable while user
  // navigates the home page but rotates on each hard reload.
  const picks = useMemo(() => {
    const pool = (photos || []).filter((p) => p && p.image_url);
    if (pool.length === 0) return [];
    const arr = pool.slice();
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr.slice(0, Math.min(5, arr.length));
  }, [photos]);

  if (picks.length === 0) return null;

  return (
    <section className="py-20 bg-white relative overflow-hidden" data-testid="trust-home-gallery">
      <div className="absolute -top-20 -right-20 w-[380px] h-[380px] rounded-full bg-[#F26419]/8 blur-[80px] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <div className="trust-eyebrow text-[10px] text-[#D90368] font-bold inline-flex items-center gap-2">
              <ImageIcon size={13} className="text-[#F26419]" /> Moments · Snapshots
            </div>
            <h2 className="trust-serif text-3xl sm:text-4xl text-[#1A1A1A] mt-2">
              From our <span className="grad-text">Gallery</span>
            </h2>
            <div className="gold-rule-solid mt-3" />
          </div>
          <Link to="gallery" data-testid="trust-home-gallery-cta" className="inline-flex items-center gap-2 bg-[#0D5C63] hover:bg-[#083E43] text-white text-[11px] font-bold uppercase tracking-wider px-5 py-2.5 rounded-full transition-colors">
            View Full Gallery <ArrowRight size={12} />
          </Link>
        </div>

        <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {picks.map((p, idx) => (
            <Link
              key={p.id || idx}
              to="gallery"
              data-testid={`trust-home-gallery-photo-${p.id || idx}`}
              className={`relative overflow-hidden rounded-xl bg-[#0D5C63]/5 group ${idx === 0 ? "col-span-2 row-span-2 aspect-[4/5] sm:col-span-1 sm:row-span-1 sm:aspect-square" : "aspect-square"}`}
            >
              <img
                src={p.image_url}
                alt={p.caption || "Gallery photograph"}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0D5C63]/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              {p.caption && (
                <div className="absolute inset-x-0 bottom-0 p-3 text-white text-[11px] leading-snug line-clamp-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  {p.caption}
                </div>
              )}
              {p.gallery_type && (
                <div className="absolute top-2 left-2 bg-white/95 text-[#0D5C63] text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded">
                  {p.gallery_type}
                </div>
              )}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function ImportantLinksSection({ links }) {
  if (!links || links.length === 0) return null;
  return (
    <section className="py-20 bg-gradient-to-br from-[#0D5C63] via-[#083E43] to-[#0D5C63] text-white relative overflow-hidden" data-testid="trust-important-links">
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#F26419]/12 blur-3xl rounded-full pointer-events-none" />
      <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-[#D90368]/12 blur-3xl rounded-full pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5">
        <div className="text-center max-w-3xl mx-auto">
          <div className="trust-eyebrow text-[10px] text-[#F26419] font-bold">Quick Access</div>
          <h2 className="trust-serif text-4xl sm:text-5xl mt-2">Important <span className="text-[#F26419]">Links</span></h2>
          <div className="mx-auto mt-3 h-[3px] w-24 bg-gradient-to-r from-[#F26419] via-[#D90368] to-transparent" />
          <p className="mt-5 text-white/80 leading-relaxed">
            Direct access to government portals, board sites, admission pages and other essential resources for students and parents.
          </p>
        </div>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {links.map((l) => (
            <a
              key={l.id}
              href={l.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`trust-important-link-${l.id}`}
              className="group bg-white/5 hover:bg-white/12 backdrop-blur border border-white/15 hover:border-[#F26419]/60 rounded-xl p-5 transition-all hover:-translate-y-1"
            >
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 shrink-0 rounded-lg bg-gradient-to-br from-[#F26419] to-[#D90368] flex items-center justify-center text-white shadow-lg shadow-[#F26419]/20">
                  <ExternalLink size={18} strokeWidth={2.2} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[15px] font-bold text-white leading-snug break-words group-hover:text-[#F26419] transition-colors">{l.label}</div>
                  <div className="text-[11px] text-white/60 mt-1 truncate" title={l.url}>{new URL(l.url).hostname.replace(/^www\./, "")}</div>
                </div>
              </div>
              <div className="mt-4 text-[10px] tracking-[0.25em] uppercase font-bold text-[#F26419]/80 group-hover:text-[#F26419] inline-flex items-center gap-1">
                Open in New Tab <ExternalLink size={10} strokeWidth={2.4} />
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function Stat({ n, l, color = "#0D5C63" }) {
  return (
    <div className="bg-white/70 backdrop-blur rounded-2xl px-4 py-3 border border-white shadow-sm">
      <div className="trust-accent text-3xl leading-none" style={{ color }}>{n}</div>
      <div className="trust-eyebrow text-[9px] text-[#5B5B5B] font-bold mt-1.5">{l}</div>
    </div>
  );
}

function NewsList({ title, items, accent, testid }) {
  const [selected, setSelected] = useState(null);
  if (items.length === 0) return null;
  const isSaffron = accent === "saffron";
  const barCls = isSaffron ? "bg-gradient-to-r from-[#F26419] to-[#D90368]" : "bg-gradient-to-r from-[#0D5C63] to-[#F26419]";
  const eyebrowCls = isSaffron ? "text-[#F26419]" : "text-[#0D5C63]";
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-3 mb-6">
        <div className={`h-1 flex-1 rounded-full ${barCls}`} />
        <div className={`trust-eyebrow text-xs font-bold ${eyebrowCls}`}>{title}</div>
        <div className={`h-1 flex-1 rounded-full ${barCls}`} />
      </div>
      <div className="space-y-5">
        {items.map((i) => {
          const photos = Array.isArray(i.photos) ? i.photos.filter((p) => p && p.url) : [];
          return (
          <article
            key={i.id}
            data-testid={`trust-news-item-${i.id}`}
            className="grid grid-cols-[110px_1fr] gap-4 group bg-[#FAFAF7] border border-[#E9E2D2] hover:border-[#F26419]/40 rounded-2xl p-3 trust-card-hover cursor-pointer"
            role="button"
            tabIndex={0}
            onClick={() => setSelected(i)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelected(i); } }}
          >
            <div className="aspect-square bg-[#0D5C63]/5 overflow-hidden rounded-xl relative">
              {i.cover_url ? <img src={i.cover_url} alt={i.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" /> : (
                <div className="w-full h-full flex items-center justify-center text-[#0D5C63]/30"><Newspaper size={24} /></div>
              )}
              {photos.length > 0 && (
                <div className="absolute bottom-1 right-1 bg-black/60 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full inline-flex items-center gap-1" data-testid={`trust-news-photo-count-${i.id}`}>
                  <ImageIcon size={9} strokeWidth={2.4} />
                  {photos.length + (i.cover_url ? 1 : 0)}
                </div>
              )}
            </div>
            <div>
              <div className="trust-eyebrow text-[10px] text-[#D90368] font-bold">{fmtDate(i.event_date)}{i.location && ` · ${i.location}`}</div>
              <h4 className="trust-serif text-lg text-[#1A1A1A] leading-tight mt-1 group-hover:text-[#0D5C63] transition-colors">{i.title}</h4>
              {i.description && <p className="text-sm text-[#3E3E3E] mt-2 line-clamp-2">{i.description.replace(/<[^>]*>/g, "")}</p>}
              {photos.length > 0 && (
                <div className="mt-2 flex items-center gap-1.5" data-testid={`trust-news-thumbs-${i.id}`}>
                  {photos.slice(0, 3).map((p, idx) => (
                    <img
                      key={idx}
                      src={p.url}
                      alt={p.caption || ""}
                      className="w-9 h-9 object-cover rounded-md ring-1 ring-[#E9E2D2]"
                      loading="lazy"
                    />
                  ))}
                  {photos.length > 3 && (
                    <span className="w-9 h-9 rounded-md bg-[#0D5C63]/10 text-[10px] text-[#0D5C63] font-semibold inline-flex items-center justify-center">
                      +{photos.length - 3}
                    </span>
                  )}
                </div>
              )}
            </div>
          </article>
          );
        })}
      </div>
      {selected && <NewsDetailModal item={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function NewsDetailModal({ item, onClose }) {
  // Photo carousel: cover_url (if any) precedes the supplementary photos so
  // the modal shows the same imagery order the admin uploaded.
  const photos = [
    ...(item.cover_url ? [{ url: item.cover_url, caption: "" }] : []),
    ...((item.photos || []).filter((p) => p && p.url)),
  ];
  const [idx, setIdx] = useState(0);
  const total = photos.length;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && total > 1) setIdx((i) => (i - 1 + total) % total);
      if (e.key === "ArrowRight" && total > 1) setIdx((i) => (i + 1) % total);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [total, onClose]);

  const cur = photos[idx];
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="trust-news-modal"
    >
      <div
        className="relative bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="trust-news-modal-close"
          className="absolute top-3 right-3 z-10 bg-white/80 hover:bg-white text-[#0D5C63] p-1.5 rounded-full shadow"
          aria-label="Close"
        >
          <X size={16} />
        </button>
        {cur && (
          <div className="relative bg-[#0D5C63]/5 aspect-[16/9] overflow-hidden">
            <img src={cur.url} alt={cur.caption || item.title} className="w-full h-full object-cover" />
            {total > 1 && (
              <>
                <button
                  type="button"
                  onClick={() => setIdx((i) => (i - 1 + total) % total)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-[#0D5C63] p-2 rounded-full shadow"
                  aria-label="Previous photo"
                  data-testid="trust-news-modal-prev"
                >
                  <ArrowLeft size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => setIdx((i) => (i + 1) % total)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-[#0D5C63] p-2 rounded-full shadow"
                  aria-label="Next photo"
                  data-testid="trust-news-modal-next"
                >
                  <ArrowRight size={16} />
                </button>
                <div className="absolute bottom-3 right-3 bg-black/60 text-white text-[11px] px-2 py-0.5 rounded-full">
                  {idx + 1} / {total}
                </div>
              </>
            )}
            {cur.caption && (
              <div className="absolute bottom-3 left-3 right-3 sm:right-24 bg-black/55 text-white text-sm px-3 py-1.5 rounded-md">
                {cur.caption}
              </div>
            )}
          </div>
        )}
        <div className="p-6 overflow-y-auto">
          <div className="trust-eyebrow text-[11px] font-bold text-[#D90368] inline-flex items-center gap-2">
            <span className="uppercase tracking-widest">{item.item_type || "News"}</span>
            {item.event_date && <><span>·</span><span>{fmtDate(item.event_date)}</span></>}
            {item.location && <><span>·</span><span>{item.location}</span></>}
          </div>
          <h3 className="trust-serif text-2xl md:text-3xl text-[#1A1A1A] mt-3 leading-tight" data-testid="trust-news-modal-title">
            {item.title}
          </h3>
          {item.description && (
            <div
              className="mt-4 text-[#3E3E3E] leading-[1.8] whitespace-pre-line"
              data-testid="trust-news-modal-desc"
            >
              {item.description.replace(/<[^>]*>/g, "")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function InstituteCard({ i }) {
  const isUrl = i.website_slug && /^https?:\/\//i.test(i.website_slug);
  const href = i.website_slug ? (isUrl ? i.website_slug : `/${i.website_slug}`) : "#";
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`trust-institute-card-${i.id}`}
      className="group relative bg-white/95 hover:bg-white rounded-2xl p-6 transition-all flex flex-col overflow-hidden trust-card-hover"
    >
      {/* Corner accent */}
      <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-[#F26419]/0 to-[#F26419]/15 rounded-bl-[80px] transition-all group-hover:from-[#F26419]/15 group-hover:to-[#D90368]/25 pointer-events-none" />
      <div className="relative h-16 flex items-center">
        {i.logo_url ? <img src={i.logo_url} alt={i.name} className="h-14 w-auto max-w-[140px] object-contain group-hover:scale-105 transition-transform" /> : <Building2 size={40} className="text-[#0D5C63]/30" />}
      </div>
      <h3 className="relative trust-serif text-xl text-[#1A1A1A] mt-4 leading-tight group-hover:text-[#0D5C63] transition-colors">{i.name}</h3>
      <div className="relative mt-2 flex flex-wrap gap-x-3 gap-y-1 trust-eyebrow text-[10px] text-[#5B5B5B] font-semibold">
        {i.type && <span>{i.type}</span>}
        {i.year_of_establishment && <span>Estd. {i.year_of_establishment}</span>}
      </div>
      <div className="relative mt-auto pt-4 trust-eyebrow text-[10px] text-[#F26419] font-bold inline-flex items-center gap-1.5 group-hover:gap-3 transition-all">
        Visit institute <ArrowRight size={11} className="group-hover:translate-x-1 transition-transform" />
      </div>
    </a>
  );
}

// Map institute name/type to a domain icon + accent colour.
const INSTITUTE_ICON_RULES = [
  { rx: /nursing|hospital|health|medical/i, icon: HeartPulse, color: "#D90368" },
  { rx: /food/i, icon: ChefHat, color: "#F26419" },
  { rx: /emerging|science|research/i, icon: Atom, color: "#7C3AED" },
  { rx: /polytechnic|diploma/i, icon: Wrench, color: "#059669" },
  { rx: /engineering|technology/i, icon: GraduationCap, color: "#0D5C63" },
  { rx: /mba|management/i, icon: Briefcase, color: "#0D5C63" },
  { rx: /mca|computer/i, icon: Monitor, color: "#0891B2" },
  { rx: /bca/i, icon: Laptop, color: "#EAB308" },
  { rx: /iti|skill/i, icon: Wrench, color: "#16A34A" },
  { rx: /hotel|hospitality|tourism/i, icon: Utensils, color: "#DC6803" },
  { rx: /preschool|daycare|kinder/i, icon: Baby, color: "#EC4899" },
  { rx: /school/i, icon: SchoolIcon, color: "#6D28D9" },
  { rx: /ycmou|distance/i, icon: BookOpen, color: "#1D4ED8" },
  { rx: /working|professional/i, icon: Presentation, color: "#B45309" },
];

function iconForInstitute(i) {
  const hay = `${i.name || ""} ${i.type || ""}`;
  const rule = INSTITUTE_ICON_RULES.find((r) => r.rx.test(hay));
  return rule || { icon: GraduationCap, color: "#0D5C63" };
}

function ExploreInstituteCard({ i }) {
  const isUrl = i.website_slug && /^https?:\/\//i.test(i.website_slug);
  const href = i.website_slug ? (isUrl ? i.website_slug : `/${i.website_slug}`) : "#";
  const { icon: Icon, color } = iconForInstitute(i);
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`trust-explore-card-${i.id}`}
      className="group relative bg-white border border-[#E9E2D2] hover:border-transparent rounded-2xl p-6 flex flex-col transition-all trust-card-hover overflow-hidden"
    >
      <div className="absolute inset-x-0 top-0 h-1 opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: `linear-gradient(90deg, ${color}, #F26419)` }} />
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0 transition-transform group-hover:scale-110" style={{ background: `${color}18`, color }}>
          <Icon size={26} strokeWidth={1.8} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="trust-eyebrow text-[9px] font-bold" style={{ color }}>{i.type || "Institute"}</div>
          <h3 className="trust-serif text-lg text-[#1A1A1A] mt-1 leading-tight group-hover:text-[#0D5C63] transition-colors line-clamp-2">{i.name}</h3>
        </div>
      </div>
      {(i.tagline || i.city || i.year_of_establishment) && (
        <p className="mt-4 text-[13px] text-[#3E3E3E] leading-relaxed line-clamp-3">
          {i.tagline || `${i.type || "Programs"} at ${i.city || "GS Mandal"}${i.year_of_establishment ? ` · Estd. ${i.year_of_establishment}` : ""}.`}
        </p>
      )}
      <div className="mt-auto pt-5 trust-eyebrow text-[10px] font-bold inline-flex items-center gap-1.5 group-hover:gap-3 transition-all" style={{ color }}>
        View details <ArrowRight size={11} className="group-hover:translate-x-1 transition-transform" />
      </div>
    </a>
  );
}

function ExploreStat({ icon: Icon, n, l, color = "#0D5C63" }) {
  return (
    <div className="flex items-center gap-4">
      <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${color}15`, color }}>
        <Icon size={22} strokeWidth={1.8} />
      </div>
      <div>
        <div className="trust-accent text-2xl leading-none" style={{ color }}>{n}</div>
        <div className="trust-eyebrow text-[9px] text-[#5B5B5B] font-bold mt-1.5">{l}</div>
      </div>
    </div>
  );
}

// ============================================================
// OVERVIEW
// ============================================================
export function TrustOverview() {
  const data = useTrust();
  const t = data.trust || {};
  const G = <strong className="font-bold text-[#0D5C63]">Gramaudyogik Shikshan Mandal (GSM)</strong>;
  const G_short = <strong className="font-bold text-[#0D5C63]">Gramaudyogik Shikshan Mandal</strong>;
  const Dr = <strong className="font-bold text-[#D90368]">Dr. Yadnaveer Kawade</strong>;
  const missionPoints = [
    "Providing accessible, affordable, and high-quality education from preschool to higher, technical, professional, and healthcare education.",
    "Empowering rural and underprivileged students through skill-oriented, industry-relevant, and employment-focused learning.",
    "Promoting innovation, entrepreneurship, research, and lifelong learning to meet the challenges of a rapidly evolving world.",
    "Integrating modern technology, practical knowledge, and experiential learning into every academic program.",
    "Developing socially responsible, ethical, and globally competent professionals committed to nation-building.",
    "Building strong partnerships with industry, academia, healthcare institutions, government, and society to enhance learning and community development.",
    "Encouraging holistic development by nurturing leadership, integrity, compassion, discipline, and environmental responsibility.",
    "Serving society through education, healthcare, community engagement, and initiatives that improve the quality of life for all.",
  ];
  return (
    <div data-testid="trust-overview">
      <PageHero eyebrow="Our Journey" title="From vision to legacy" sub="A five-decade story of building rural-first, industry-oriented education across Marathwada." />
      <section className="max-w-6xl mx-auto px-5 py-14">
        <div className="grid md:grid-cols-3 gap-10">
          <div className="md:col-span-2">
            <div className="trust-eyebrow text-[10px] text-[#D90368] font-bold">The GSM Journey</div>
            <h2 className="trust-serif text-2xl sm:text-3xl text-[#1A1A1A] mt-2 leading-tight rani-underline inline-block">A Legacy of Service Since 1972</h2>

            <div className="mt-7 space-y-4 text-[14px] sm:text-[15px] text-[#3E3E3E] leading-[1.75] text-justify" data-testid="trust-overview-story" style={{ hyphens: "auto", textJustify: "inter-word" }}>
              <p>
                <span className="trust-accent float-left text-5xl leading-none mr-3 mt-1 text-[#0D5C63]">G</span>
                ramaudyogik Shikshan Mandal (GSM) was founded in <strong className="text-[#0D5C63]">1972</strong> by visionary educationist {Dr}, with a mission to empower rural and underprivileged youth through quality technical, professional, and skill-based education. At a time when access to technical education was largely limited to metropolitan cities such as Mumbai and Pune, {Dr} envisioned an institution that would bring industry-oriented education closer to students from rural and semi-urban regions. He firmly believed that education should not merely award degrees but should equip young minds with practical skills, professional competence, ethical values, and the confidence to become self-reliant contributors to society and the nation&apos;s development.
              </p>

              <p>
                The journey began in <strong className="text-[#0D5C63]">Rotegaon</strong>, where {Dr} laid the foundation of an educational movement dedicated to creating employment-oriented learning opportunities for rural youth. Guided by his vision, the trust was formally registered in <strong className="text-[#0D5C63]">1976</strong>, and its activities gradually expanded to Chhatrapati Sambhajinagar (formerly Aurangabad), where the institution found a strong platform for sustainable growth. The first major initiative was the establishment of a Technical High School, introducing technical education to students from Classes VIII to X at an early stage of their academic journey. This was followed by Technical Higher Secondary education, the College of Applied Sciences, Polytechnic, and eventually Engineering education, reflecting a carefully planned and progressive expansion to meet the evolving educational needs of society. The journey from trust registration in 1976 to establishing the Engineering College by 1983–84 marked a significant milestone in the institution&apos;s development.
              </p>

              <p>
                From its inception, {Dr} emphasized that educational institutions must remain aligned with technological advancements and industry requirements. Long before technology became central to higher education, {G} focused on building modern laboratories, introducing advanced computing facilities, strengthening industry interaction, and creating opportunities for students to gain practical exposure. This forward-looking approach enabled the institution to become one of the pioneers in introducing several emerging technologies and industry-oriented initiatives in the <strong className="text-[#0D5C63]">Marathwada region</strong>, ensuring that students received education that was both academically strong and professionally relevant.
              </p>

              <p>
                As the institution continued to grow, its vision expanded beyond engineering to embrace a multidisciplinary approach to education. Under {Dr}&apos;s leadership, {G_short} established institutions in <span className="font-semibold text-[#0D5C63]">Engineering, Polytechnic, ITI, Applied Sciences, Management, Computer Applications, Nursing, Hospitality, School Education, Healthcare</span>, and several other professional disciplines. The establishment of <strong className="text-[#D90368]">MIT Hospital</strong> further reflected the trust&apos;s commitment to serving society beyond education by providing quality healthcare alongside academic excellence.
              </p>

              <p>
                The remarkable growth of {G_short} has been shaped by perseverance, visionary leadership, and an unwavering commitment to educational excellence despite financial, administrative, and regulatory challenges encountered over the decades. Every milestone achieved has reinforced the founding philosophy of creating opportunities for deserving students irrespective of their social or economic background. What began as a modest initiative in Rotegaon has evolved into one of the region&apos;s most respected educational trusts, nurturing thousands of students and professionals who continue to contribute across diverse fields in India and abroad.
              </p>

              <p>
                Today, {G_short} stands as a vibrant multidisciplinary educational organization offering learning opportunities from preschool to postgraduate and professional education. While the institution has expanded significantly in scale and reputation, it continues to uphold the ideals envisioned by {Dr}—providing accessible, quality education, promoting innovation and entrepreneurship, nurturing ethical leadership, and empowering generations of students to build a better future for themselves and for society.
              </p>
            </div>
          </div>
          <aside className="space-y-3">
            <Fact k="Founded" v="1972" />
            <Fact k="Registered" v="1976" />
            <Fact k="Founder" v="Dr. Yadnaveer Kawade" />
            <Fact k="Registration No." v={t.registration_number} />
            <Fact k="PAN" v={t.pan_number} />
            <Fact k="GST" v={t.gst_number} />
            <Fact k="Trust Type" v={t.trust_type} />
            <Fact k="Registered Office" v={[t.reg_city, t.reg_state].filter(Boolean).join(", ")} />
          </aside>
        </div>

        {/* VISION */}
        <div className="mt-14 bg-gradient-to-br from-[#F26419] to-[#D90368] text-white rounded-2xl p-8 sm:p-10 relative overflow-hidden shadow-lg" data-testid="trust-vision">
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
          <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
          <div className="relative">
            <div className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-white/20 backdrop-blur">
              <Quote size={20} className="text-white" />
            </div>
            <div className="trust-eyebrow text-[10px] font-bold text-white/90 mt-4">Our Vision</div>
            <h3 className="trust-serif text-2xl sm:text-3xl mt-1">A globally respected institution</h3>
            <p className="mt-4 text-[14px] sm:text-[15px] leading-[1.8] text-white/95 text-justify max-w-4xl">
              To be a globally respected institution that transforms lives through <span className="font-bold">inclusive, innovative, and value-based education</span>, empowering individuals with knowledge, skills, character, and compassion to lead, innovate, and contribute meaningfully to <span className="font-bold">society and nation-building</span>.
            </p>
          </div>
        </div>

        {/* MISSION */}
        <div className="mt-8 bg-gradient-to-br from-[#0D5C63] to-[#083E43] text-white rounded-2xl p-8 sm:p-10 relative overflow-hidden shadow-lg" data-testid="trust-mission">
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#F26419]/15 blur-2xl" />
          <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-[#D90368]/15 blur-2xl" />
          <div className="relative">
            <div className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-white/20 backdrop-blur">
              <Sparkles size={20} className="text-[#F26419]" />
            </div>
            <div className="trust-eyebrow text-[10px] font-bold text-[#F26419] mt-4">Our Mission</div>
            <h3 className="trust-serif text-2xl sm:text-3xl mt-1">Gramaudyogik Shikshan Mandal is committed to:</h3>
            <ol className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-4 counter-reset-[m]" data-testid="trust-mission-list">
              {missionPoints.map((point, idx) => (
                <li key={idx} data-testid={`trust-mission-point-${idx + 1}`} className="flex gap-3 items-start">
                  <span className="shrink-0 w-8 h-8 rounded-full bg-[#F26419] text-white text-[13px] font-bold flex items-center justify-center shadow-md shadow-[#F26419]/25 trust-accent">
                    {idx + 1}
                  </span>
                  <span className="text-[13.5px] sm:text-[14px] leading-[1.7] text-white/95 pt-0.5 text-justify">{point}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>
    </div>
  );
}

function Fact({ k, v }) {
  if (!v) return null;
  return (
    <div className="bg-white border-l-4 border-[#F26419] rounded-r-xl px-4 py-3 shadow-sm hover:shadow-md hover:border-[#D90368] transition-all">
      <div className="trust-eyebrow text-[10px] text-[#5B5B5B] font-bold">{k}</div>
      <div className="text-[#1A1A1A] font-semibold text-sm mt-1">{v}</div>
    </div>
  );
}

function Card({ title, children, gold, navy }) {
  const cls = gold ? "bg-gradient-to-br from-[#F26419] to-[#D90368] text-white" : navy ? "bg-gradient-to-br from-[#0D5C63] to-[#083E43] text-white" : "bg-white text-[#1A1A1A]";
  return (
    <div className={`${cls} p-8 relative rounded-2xl shadow-lg overflow-hidden`}>
      <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-white/10 blur-xl" />
      <div className="relative">
        <div className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-white/20 backdrop-blur">
          <Quote size={20} className="text-white" />
        </div>
        <h3 className="trust-serif text-2xl mt-4">{title}</h3>
        <div className={`text-[15px] leading-relaxed mt-3 ${gold || navy ? "text-white/95" : ""}`}>
          {children}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// FOUNDER INSPIRATION (single record + photo slider)
// ============================================================
export function TrustPioneers() {
  const data = useTrust();
  const list = data.pioneers || [];
  // Prefer the first Active record — this page shows the single "Founder Inspiration".
  const founder = useMemo(() => list.find((p) => (p.status || "Active") === "Active") || list[0] || null, [list]);
  const slides = useMemo(() => {
    if (!founder) return [];
    const gallery = Array.isArray(founder.photos) ? founder.photos.filter((p) => p && p.url) : [];
    if (gallery.length > 0) return gallery.map((p, i) => ({ id: `${founder.id}-p${i}`, image_url: p.url, caption: p.caption || "" }));
    if (founder.photo_url) return [{ id: `${founder.id}-lead`, image_url: founder.photo_url, caption: "" }];
    return [];
  }, [founder]);

  const [slide, setSlide] = useState(0);
  const total = slides.length;
  useEffect(() => {
    if (total < 2) return;
    const id = setInterval(() => setSlide((s) => (s + 1) % total), 6000);
    return () => clearInterval(id);
  }, [total]);
  useEffect(() => { setSlide(0); }, [founder?.id]);

  return (
    <div data-testid="trust-pioneers">
      <PageHero eyebrow="Founder Inspiration" title={founder?.name || "Founder Inspiration"} sub="The vision that shaped Gramaudyogik Shikshan Mandal." />
      <section className="relative max-w-7xl mx-auto px-5 py-16 lg:py-20">
        {!founder ? (
          <EmptyState icon={Award} msg="The Founder Inspiration story will appear here once published." />
        ) : (
          <div className="grid lg:grid-cols-[420px_1fr] gap-10 lg:gap-14 items-start">
            {/* Photo slider */}
            <div className="lg:sticky lg:top-24">
              <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl ring-4 ring-white bg-gradient-to-br from-[#0D5C63] to-[#083E43]" data-testid="trust-founder-slider">
                {slides.length > 0 ? (
                  slides.map((s, i) => (
                    <div key={s.id} className="absolute inset-0 transition-opacity duration-1000" style={{ opacity: i === slide ? 1 : 0 }} data-testid={`trust-founder-slide-${i}`}>
                      <img src={s.image_url} alt={s.caption || founder.name} className="w-full h-full object-cover object-center" />
                      {s.caption && (
                        <>
                          <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#0D5C63] via-[#0D5C63]/70 to-transparent" />
                          <div className="absolute bottom-5 left-5 right-5 text-white">
                            <div className="trust-eyebrow text-[10px] text-[#F26419] font-bold">Moment</div>
                            <div className="trust-serif text-lg mt-1 leading-tight">{s.caption}</div>
                          </div>
                        </>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-white/70">
                    <Users size={72} strokeWidth={1} />
                  </div>
                )}
                {total > 1 && (
                  <>
                    {/* Nav arrows */}
                    <button
                      type="button"
                      onClick={() => setSlide((s) => (s - 1 + total) % total)}
                      aria-label="previous slide"
                      data-testid="trust-founder-prev"
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/85 hover:bg-white text-[#0D5C63] flex items-center justify-center shadow-md transition-transform hover:-translate-x-0.5"
                    >
                      <ArrowLeft size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setSlide((s) => (s + 1) % total)}
                      aria-label="next slide"
                      data-testid="trust-founder-next"
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/85 hover:bg-white text-[#0D5C63] flex items-center justify-center shadow-md transition-transform hover:translate-x-0.5"
                    >
                      <ArrowRight size={16} />
                    </button>
                    <div className="absolute bottom-4 right-4 flex items-center gap-1.5" data-testid="trust-founder-dots">
                      {slides.map((_, i) => (
                        <button key={i} onClick={() => setSlide(i)} aria-label={`slide ${i + 1}`} className={`h-1.5 rounded-full transition-all ${i === slide ? "w-8 bg-[#F26419]" : "w-3 bg-white/50 hover:bg-white/80"}`} data-testid={`trust-founder-dot-${i}`} />
                      ))}
                    </div>
                  </>
                )}
              </div>
              {/* decorative frame accents */}
              <div className="hidden lg:block relative">
                <div className="absolute -top-6 -left-4 w-16 h-16 border-4 border-[#F26419] rounded-2xl -z-10" />
                <div className="absolute -bottom-4 -right-4 w-20 h-20 bg-[#D90368]/25 rounded-2xl -z-10" />
              </div>
              {founder.name && (
                <div className="mt-6 text-center">
                  <div className="trust-eyebrow text-[10px] text-[#D90368] font-bold">Founder · Visionary Educationist</div>
                  <div className="trust-serif text-xl text-[#1A1A1A] mt-1">{founder.name}</div>
                  {total > 0 && <div className="mt-2 text-[11px] text-[#5B5B5B]">{total} photograph{total > 1 ? "s" : ""} · auto-slides every 6 seconds</div>}
                </div>
              )}
            </div>

            {/* Narrative */}
            <div className="lg:pl-2 min-w-0">
              <div className="trust-eyebrow text-[10px] text-[#D90368] font-bold flex items-center gap-2">
                <Sparkles size={13} className="text-[#F26419]" /> The Inspiration Behind GSM
              </div>
              <h2 className="trust-serif text-3xl sm:text-4xl text-[#1A1A1A] mt-2 leading-tight rani-underline inline-block">{founder.name}</h2>
              {founder.description ? (
                <div
                  className="mt-6 text-[15px] text-[#3E3E3E] leading-[1.8] prose prose-lg max-w-none prose-headings:trust-serif prose-headings:text-[#1A1A1A] prose-p:text-[#3E3E3E] prose-a:text-[#F26419] prose-strong:text-[#0D5C63] prose-strong:font-bold"
                  data-testid="trust-founder-description"
                  dangerouslySetInnerHTML={{ __html: renderHtml(founder.description) }}
                />
              ) : (
                <p className="mt-6 text-[15px] text-[#5B5B5B]">The founder&apos;s story will be shared here soon.</p>
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

// ============================================================
// TRUSTEES
// ============================================================
export function TrustTrustees() {
  const data = useTrust();
  const trustees = data.trustees || [];
  return (
    <div data-testid="trust-trustees">
      <PageHero eyebrow="Governing Body" title="Our Trustees" sub="Stewards of the trust — guiding policy, governance and growth." />
      <section className="max-w-7xl mx-auto px-5 py-14">
        {trustees.length === 0 ? (
          <EmptyState icon={Users} msg="Trustee profiles will be published shortly." />
        ) : (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {trustees.map((m) => (
              <article key={m.id} data-testid={`trust-trustee-${m.id}`} className="group bg-white rounded-2xl overflow-hidden trust-card-hover border border-[#E9E2D2] hover:border-[#0D5C63]/30">
                <div className="aspect-square bg-gradient-to-br from-[#0D5C63]/5 to-[#F26419]/10 overflow-hidden">
                  {m.photo_url ? <img src={m.photo_url} alt={m.name} className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500" /> : (
                    <div className="w-full h-full flex items-center justify-center text-[#0D5C63]/25"><Users size={48} strokeWidth={1} /></div>
                  )}
                </div>
                <div className="p-5">
                  {m.designation && <div className="trust-eyebrow text-[10px] text-[#D90368] font-bold">{m.designation}</div>}
                  <h3 className="trust-serif text-lg text-[#1A1A1A] leading-tight mt-1 group-hover:text-[#0D5C63] transition-colors">{m.name}</h3>
                  {m.email && <div className="mt-2 text-[11px] text-[#5B5B5B] truncate">{m.email}</div>}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// ============================================================
// INSTITUTES
// ============================================================
function JourneyTree({ institutes = [] }) {
  // Group institutes by type — categories come dynamically from the database.
  const categories = useMemo(() => {
    const map = new Map();
    institutes.forEach((i) => {
      const key = (i.type || "Other").trim();
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(i);
    });
    // Sort each bucket by year (oldest first), then sort category groups by their earliest institute year.
    const cats = Array.from(map.entries()).map(([type, list]) => {
      const sorted = [...list].sort((a, b) => (Number(a.year_of_establishment) || 9999) - (Number(b.year_of_establishment) || 9999));
      const earliest = Number(sorted[0]?.year_of_establishment) || 9999;
      return { type, items: sorted, earliest, style: styleForType(type) };
    });
    cats.sort((a, b) => a.earliest - b.earliest);
    return cats;
  }, [institutes]);

  const decades = useMemo(() => {
    // Bucket institutes into decades for the timeline.
    const buckets = new Map();
    institutes.forEach((i) => {
      const y = Number(i.year_of_establishment);
      if (!y) return;
      const dec = Math.floor(y / 10) * 10;
      if (!buckets.has(dec)) buckets.set(dec, []);
      buckets.get(dec).push(i);
    });
    return Array.from(buckets.entries()).sort((a, b) => a[0] - b[0]);
  }, [institutes]);

  const slugOf = (i) => i.website_slug || i.slug || "";

  return (
    <section id="journey-tree" className="py-20 bg-gradient-to-b from-white via-[#FAFAF7] to-white" data-testid="trust-journey-tree">
      <div className="max-w-7xl mx-auto px-5">
        <div className="text-center max-w-3xl mx-auto">
          <div className="trust-eyebrow text-[10px] text-[#D90368] font-bold">Our Growth Story</div>
          <h2 className="trust-serif text-4xl sm:text-5xl text-[#1A1A1A] mt-2">The GS Mandal <span className="grad-text">Journey Tree</span></h2>
          <div className="gold-rule-solid mx-auto mt-3" />
          <p className="mt-5 text-[#3E3E3E] leading-relaxed">
            A visual walk through the expansion of Gramaudyogik Shikshan Mandal — from a single school in 1979 to a multi-disciplinary federation of {institutes.length} institutes across Maharashtra and beyond.
          </p>
        </div>

        {/* Root card */}
        <div className="mt-12 flex flex-col items-center">
          <div className="relative bg-gradient-to-br from-[#0D5C63] via-[#0D5C63] to-[#083E43] text-white rounded-2xl shadow-2xl shadow-[#0D5C63]/30 px-8 py-6 max-w-md text-center border-4 border-[#F26419]/50">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#F26419] text-white text-[10px] tracking-[0.25em] uppercase font-bold px-3 py-1 rounded-full">Since 1975</div>
            <div className="trust-serif text-2xl font-black leading-tight mt-2">Gramaudyogik Shikshan Mandal</div>
            <div className="text-white/85 text-[13px] mt-1">Chhatrapati Sambhajinagar</div>
            <div className="mt-3 inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase font-bold bg-white/10 border border-white/20 px-3 py-1 rounded-full">
              A Charitable Educational Trust
            </div>
          </div>
          {/* Connector line down */}
          <div className="w-px h-10 bg-gradient-to-b from-[#0D5C63] to-[#F26419]" />
        </div>

        {/* Decade timeline — the "branches" of the tree */}
        <div className="relative">
          {/* Vertical spine */}
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[#F26419] via-[#D90368] to-[#0D5C63]" />
          <div className="space-y-16 md:space-y-24">
            {decades.map(([decade, list], idx) => {
              const left = idx % 2 === 0;
              return (
                <div key={decade} className="relative md:grid md:grid-cols-2 md:gap-16" data-testid={`journey-decade-${decade}`}>
                  {/* Decade label */}
                  <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 top-6 z-10 bg-white border-4 border-[#F26419] rounded-full w-24 h-24 items-center justify-center shadow-lg">
                    <div className="text-center leading-tight">
                      <div className="text-[9px] tracking-[0.2em] uppercase text-[#D90368] font-bold">Decade</div>
                      <div className="trust-serif text-lg font-black text-[#1A1A1A]">{decade}s</div>
                    </div>
                  </div>
                  {/* mobile decade tag */}
                  <div className="md:hidden mb-4 inline-flex items-center gap-2 bg-[#F26419] text-white text-[11px] tracking-[0.2em] uppercase font-bold px-3 py-1 rounded-full">Decade · {decade}s</div>

                  {/* Institute cards for the decade */}
                  <div className={`${left ? "md:col-start-1 md:pr-16" : "md:col-start-2 md:pl-16 md:pt-10"} space-y-3`}>
                    {list.map((i) => {
                      const slug = slugOf(i);
                      const card = (
                        <div className="group relative bg-white border border-[#E9E2D2] rounded-xl p-4 hover:shadow-lg hover:border-[#F26419] transition-all">
                          <div className="flex items-center gap-3">
                            <div className="w-11 h-11 shrink-0 rounded-lg bg-gradient-to-br from-[#0D5C63]/10 to-[#F26419]/10 flex items-center justify-center overflow-hidden">
                              {i.logo_url ? (
                                <img src={i.logo_url} alt={i.name} className="w-full h-full object-contain p-1" />
                              ) : (
                                <GraduationCap size={22} className="text-[#0D5C63]" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-[10px] tracking-[0.2em] uppercase text-[#D90368] font-bold">{i.year_of_establishment || "—"}</div>
                              <div className="trust-serif text-[15px] font-bold text-[#1A1A1A] leading-tight truncate" title={i.name}>{(i.short_name || i.name || "").trim()}</div>
                              <div className="text-[11px] text-[#5B5B5B] truncate">{i.type || "Institute"}{i.city ? ` · ${i.city}` : ""}</div>
                            </div>
                          </div>
                        </div>
                      );
                      return slug ? (
                        <a key={i.id} href={`/${slug}`} data-testid={`journey-inst-${slug}`} className="block">{card}</a>
                      ) : <div key={i.id}>{card}</div>;
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Categorised list */}
        <div className="mt-20">
          <div className="text-center max-w-3xl mx-auto">
            <div className="trust-eyebrow text-[10px] text-[#D90368] font-bold">Categorised View</div>
            <h3 className="trust-serif text-3xl sm:text-4xl text-[#1A1A1A] mt-2">Constituent Institutes by <span className="grad-text">Discipline</span></h3>
            <div className="gold-rule-solid mx-auto mt-3" />
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
            {categories.map((cat) => (
              <div key={cat.type} className="bg-white border border-[#E9E2D2] rounded-2xl overflow-hidden shadow-sm" data-testid={`journey-category-${cat.type.replace(/\s+/g, "-")}`}>
                <div className="px-5 py-4 flex items-center justify-between" style={{ background: `linear-gradient(90deg, ${cat.style.accent}15, transparent)`, borderBottom: `2px solid ${cat.style.accent}40` }}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center text-lg" style={{ backgroundColor: `${cat.style.accent}20`, color: cat.style.accent }}>{cat.style.icon}</div>
                    <div>
                      <div className="text-[10px] tracking-[0.25em] uppercase font-bold" style={{ color: cat.style.accent }}>{cat.items.length} Institute{cat.items.length !== 1 ? "s" : ""}</div>
                      <div className="trust-serif text-lg text-[#1A1A1A] font-bold">{cat.type}</div>
                    </div>
                  </div>
                </div>
                <div className="divide-y divide-[#F0EBE0]">
                  {cat.items.map((i) => {
                    const slug = slugOf(i);
                    const row = (
                      <div className="px-5 py-3 flex items-center justify-between gap-3 hover:bg-[#FAFAF7] transition-colors">
                        <div className="min-w-0">
                          <div className="text-[13px] font-semibold text-[#1A1A1A] truncate">{i.short_name || i.name}</div>
                          <div className="text-[11px] text-[#5B5B5B] truncate">{i.city || "—"}</div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-[10px] tracking-[0.2em] uppercase font-bold" style={{ color: cat.style.accent }}>Estd.</div>
                          <div className="text-sm font-mono text-[#1A1A1A]">{i.year_of_establishment || "—"}</div>
                        </div>
                      </div>
                    );
                    return slug ? (
                      <a key={i.id} href={`/${slug}`} className="block" data-testid={`journey-cat-inst-${slug}`}>{row}</a>
                    ) : <div key={i.id}>{row}</div>;
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Milestone stats */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4">
          <MilestoneStat n={institutes.length} label="Institutes" accent="#0D5C63" />
          <MilestoneStat n={categories.length} label="Disciplines" accent="#F26419" />
          <MilestoneStat n={decades.length} label="Decades of Growth" accent="#D90368" />
          <MilestoneStat n="30k+" label="Alumni Impact" accent="#7B4E96" />
        </div>
      </div>
    </section>
  );
}

function MilestoneStat({ n, label, accent }) {
  return (
    <div className="bg-white border border-[#E9E2D2] rounded-xl p-5 text-center shadow-sm">
      <div className="trust-serif text-3xl font-black" style={{ color: accent }}>{n}</div>
      <div className="text-[10px] tracking-[0.25em] uppercase text-[#5B5B5B] font-bold mt-1">{label}</div>
    </div>
  );
}

export function TrustInstitutes() {
  const data = useTrust();
  const list = useMemo(() => byYear(data.institutes || []), [data.institutes]);
  return (
    <div data-testid="trust-institutes">
      <PageHero eyebrow="A Federation of Excellence" title="Our Institutes" sub={`${list.length} campuses · One philosophy of learning · From 1979 to today.`} />
      <section className="max-w-7xl mx-auto px-5 py-14">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {list.map((i) => <InstituteCard key={i.id} i={i} />)}
        </div>
      </section>
    </div>
  );
}

// ============================================================
// OUR TEAM (PRINCIPALS)
// ============================================================
export function TrustTeam() {
  const data = useTrust();
  const principals = data.principals || [];
  return (
    <div data-testid="trust-team">
      <PageHero eyebrow="Our Team" title="Principals" sub="The academic leaders steering each of our institutes." />
      <section className="max-w-7xl mx-auto px-5 py-14">
        {principals.length === 0 ? (
          <EmptyState icon={GraduationCap} msg="Principal profiles will appear here once published in the Employees module with a “Principal” designation." />
        ) : (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {principals.map((p) => (
              <article key={p.id || p.employee_code} data-testid={`trust-principal-${p.id || p.employee_code}`} className="group bg-white rounded-2xl overflow-hidden trust-card-hover border border-[#E9E2D2]">
                <div className="aspect-square bg-gradient-to-br from-[#F26419]/10 to-[#D90368]/10 overflow-hidden">
                  {p.photo_url ? <img src={p.photo_url} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : (
                    <div className="w-full h-full flex items-center justify-center text-[#0D5C63]/25"><GraduationCap size={48} strokeWidth={1} /></div>
                  )}
                </div>
                <div className="p-5">
                  <div className="trust-eyebrow text-[10px] text-[#D90368] font-bold">{p.designation || "Principal"}</div>
                  <h3 className="trust-serif text-lg text-[#1A1A1A] leading-tight mt-1 group-hover:text-[#0D5C63] transition-colors">{p.name}</h3>
                  {p.qualification && <div className="text-[11px] text-[#5B5B5B] mt-1.5">{p.qualification}</div>}
                  {p.email && <div className="mt-1 text-[11px] text-[#5B5B5B] truncate">{p.email}</div>}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// ============================================================
// GALLERY
// ============================================================
export function TrustGallery() {
  const data = useTrust();
  const photos = data.gallery || [];
  const [lb, setLb] = useState(-1);

  const types = useMemo(() => {
    const s = new Set();
    photos.forEach((p) => p.gallery_type && s.add(p.gallery_type));
    return Array.from(s);
  }, [photos]);
  const [filter, setFilter] = useState("");
  const visible = filter ? photos.filter((p) => p.gallery_type === filter) : photos;

  return (
    <div data-testid="trust-gallery">
      <PageHero eyebrow="Moments" title="Gallery" sub="Photographs from across the GS Mandal family." />
      <section className="max-w-7xl mx-auto px-5 py-12">
        {types.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8">
            <button type="button" onClick={() => { setFilter(""); setLb(-1); }} className={`trust-eyebrow text-[10px] px-4 py-2 font-bold rounded-full transition-all ${filter === "" ? "bg-gradient-to-r from-[#F26419] to-[#D90368] text-white shadow-md" : "bg-white text-[#0D5C63] border border-[#E9E2D2] hover:border-[#F26419]"}`}>All ({photos.length})</button>
            {types.map((t) => (
              <button key={t} type="button" onClick={() => { setFilter(t); setLb(-1); }} className={`trust-eyebrow text-[10px] px-4 py-2 font-bold rounded-full transition-all ${filter === t ? "bg-[#0D5C63] text-white shadow-md" : "bg-white text-[#0D5C63] border border-[#E9E2D2] hover:border-[#F26419]"}`}>{t} ({photos.filter((p) => p.gallery_type === t).length})</button>
            ))}
          </div>
        )}
        {visible.length === 0 ? (
          <EmptyState icon={ImageIcon} msg="Photographs will be published here soon." />
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {visible.map((g, i) => (
              <button key={g.id} onClick={() => setLb(i)} className="aspect-square overflow-hidden bg-[#0D5C63]/5 rounded-xl group relative" data-testid={`trust-gallery-photo-${g.id}`}>
                <img src={g.image_url} alt={g.caption || ""} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0D5C63]/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            ))}
          </div>
        )}
        {lb >= 0 && visible[lb] && (
          <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setLb(-1)}>
            <button className="absolute top-4 right-4 text-white" onClick={() => setLb(-1)} aria-label="Close"><X size={28} /></button>
            <button className="absolute left-4 top-1/2 -translate-y-1/2 text-white" onClick={(e) => { e.stopPropagation(); setLb((lb - 1 + visible.length) % visible.length); }} aria-label="Previous"><ArrowLeft size={28} /></button>
            <button className="absolute right-4 top-1/2 -translate-y-1/2 text-white" onClick={(e) => { e.stopPropagation(); setLb((lb + 1) % visible.length); }} aria-label="Next"><ArrowRight size={28} /></button>
            <figure onClick={(e) => e.stopPropagation()} className="max-w-5xl">
              <img src={visible[lb]?.image_url} alt={visible[lb]?.caption || ""} className="max-w-full max-h-[80vh] object-contain" />
              {visible[lb]?.caption && <figcaption className="mt-3 text-center text-white/85 text-sm">{visible[lb].caption}</figcaption>}
            </figure>
          </div>
        )}
      </section>
    </div>
  );
}

// ============================================================
// KNOW YOUR FEES — public fee-structure viewer
// ============================================================
function currentAcademicYear() {
  const d = new Date();
  const y = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return `${y}-${String((y + 1) % 100).padStart(2, "0")}`;
}
function feesFormatINR(n) {
  const v = Number.isFinite(n) ? Number(n) : 0;
  return v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function TrustFees() {
  const data = useTrust();
  const allInstitutes = useMemo(() => byYear(data.institutes || []), [data.institutes]);
  const [ay] = useState(currentAcademicYear());
  const [feeInstIds, setFeeInstIds] = useState(null); // null = loading, [] = loaded

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/public/fees/institutes");
        if (!alive) return;
        setFeeInstIds(Array.isArray(data?.institute_ids) ? data.institute_ids : []);
      } catch {
        if (alive) setFeeInstIds([]);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Only show institutes whose fees are actually configured.
  const institutes = useMemo(() => {
    if (feeInstIds === null) return [];
    const allow = new Set(feeInstIds);
    return allInstitutes.filter((i) => allow.has(i.id));
  }, [allInstitutes, feeInstIds]);

  const defaultInst = useMemo(
    () => institutes.find((i) => /autonomous/i.test(i.name || "")) || institutes[0],
    [institutes]
  );
  const [instId, setInstId] = useState("");
  useEffect(() => { if (defaultInst && !instId) setInstId(defaultInst.id); }, [defaultInst, instId]);
  // Reset instId if the currently selected institute no longer exists in the filtered list.
  useEffect(() => {
    if (instId && institutes.length > 0 && !institutes.some((i) => i.id === instId)) {
      setInstId(institutes[0].id);
    }
  }, [institutes, instId]);

  const [masters, setMasters] = useState({ fee_admission_process: [], fee_course_type: [], fee_category: [] });
  const [available, setAvailable] = useState({ course_types: [], admission_processes: [], categories: [] });
  const [admProc, setAdmProc] = useState("");
  const [courseType, setCourseType] = useState("");
  const [category, setCategory] = useState("");
  const [table, setTable] = useState({ found: false, items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");

  const isInstituteLevel = /institute\s*level/i.test(admProc || "");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [m, av] = await Promise.all([
          api.get("/public/fees/masters"),
          instId ? api.get("/public/fees/available", { params: { institute_id: instId, academic_year: ay } }) : Promise.resolve({ data: { course_types: [], admission_processes: [], categories: [] } }),
        ]);
        if (!alive) return;
        setMasters(m.data || {});
        setAvailable(av.data || { course_types: [], admission_processes: [], categories: [] });
        const capMatch = (av.data?.admission_processes || []).find((x) => /cap/i.test(x))
          || (m.data?.fee_admission_process || []).find((x) => /cap/i.test(x?.name))?.name
          || "";
        setAdmProc(capMatch);
        setCourseType("");
        setCategory("");
      } catch { /* silent */ }
    })();
    return () => { alive = false; };
  }, [instId, ay]);

  useEffect(() => {
    if (!instId || !ay || !courseType || !admProc) {
      setTable({ found: false, items: [], total: 0 });
      setStatus("");
      return;
    }
    const effCategory = isInstituteLevel ? "" : category;
    if (!isInstituteLevel && !effCategory) {
      setTable({ found: false, items: [], total: 0 });
      setStatus("");
      return;
    }
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/public/fees", {
          params: {
            institute_id: instId, academic_year: ay,
            course_type: courseType, admission_process: admProc,
            category: effCategory,
          },
        });
        if (!alive) return;
        setTable({ found: !!data?.found, items: data?.items || [], total: Number(data?.total) || 0 });
        setStatus(data?.found ? "" : "not_published");
      } catch {
        if (alive) { setTable({ found: false, items: [], total: 0 }); setStatus("error"); }
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [instId, ay, courseType, admProc, category, isInstituteLevel]);

  const activeInst = institutes.find((i) => i.id === instId) || defaultInst;
  const admProcOptions = available.admission_processes.length > 0
    ? available.admission_processes
    : (masters.fee_admission_process || []).map((x) => x.name);
  const courseTypeOptions = available.course_types.length > 0
    ? available.course_types
    : (masters.fee_course_type || []).map((x) => x.name);
  const categoryOptions = available.categories.length > 0
    ? available.categories
    : (masters.fee_category || []).map((x) => x.name);

  return (
    <div data-testid="trust-fees">
      <PageHero eyebrow="Know Your Fees" title="Institute Fee Structure" sub="Transparent, categorised fee tables across our institutes." />
      <section className="max-w-6xl mx-auto px-5 py-14">
        {feeInstIds === null ? (
          <div className="bg-white border border-[#E9E2D2] rounded-2xl p-10 text-center text-sm text-[#5B5B5B]" data-testid="trust-fees-loading">Loading institutes…</div>
        ) : institutes.length === 0 ? (
          <div className="bg-white border-2 border-dashed border-[#E9E2D2] rounded-2xl p-12 text-center" data-testid="trust-fees-none">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-[#F26419]/15 to-[#D90368]/15 text-[#0D5C63]">
              <Sparkles size={22} />
            </div>
            <div className="trust-serif text-lg text-[#1A1A1A] mt-4">Fee schedules are being finalised</div>
            <p className="text-sm text-[#5B5B5B] mt-1 max-w-lg mx-auto">No institute has published its fee structure for {ay} yet. Please check back shortly or contact the trust office.</p>
          </div>
        ) : (<>
        {institutes.length > 1 && (
          <div className="mb-6 flex flex-wrap items-center gap-3">
            <span className="trust-eyebrow text-[10px] text-[#5B5B5B] font-bold">Choose Institute</span>
            <select
              data-testid="trust-fees-institute"
              value={instId}
              onChange={(e) => setInstId(e.target.value)}
              className="bg-white border border-[#E9E2D2] rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D5C63]"
            >
              {institutes.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
            </select>
          </div>
        )}

        <div className="bg-gradient-to-br from-[#0D5C63] to-[#083E43] text-white rounded-2xl p-8 relative overflow-hidden shadow-lg" data-testid="trust-fees-header">
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#F26419]/15 blur-2xl" />
          <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-[#D90368]/15 blur-2xl" />
          <div className="relative">
            <div className="trust-eyebrow text-[10px] font-bold text-[#F26419]">Fee Structure · Academic Year</div>
            <h2 className="trust-serif text-3xl sm:text-4xl mt-2 leading-tight">
              {activeInst?.name || "Institute"} <span className="text-[#F26419]">({ay})</span>
            </h2>
            <p className="mt-3 text-white/85 text-sm max-w-3xl">Select an <span className="font-semibold">Admission Process</span>, <span className="font-semibold">Course Type</span> and <span className="font-semibold">Category</span> below to view the published fee schedule. If Admission Process is <em>Institute Level</em>, Category is not required.</p>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <label className="block">
            <div className="trust-eyebrow text-[10px] text-[#5B5B5B] font-bold mb-1.5">Admission Process</div>
            <select data-testid="trust-fees-adm-process" value={admProc} onChange={(e) => setAdmProc(e.target.value)} className="w-full bg-white border border-[#E9E2D2] rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D5C63]">
              <option value="">Select…</option>
              {admProcOptions.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="trust-eyebrow text-[10px] text-[#5B5B5B] font-bold mb-1.5">Course Type</div>
            <select data-testid="trust-fees-course-type" value={courseType} onChange={(e) => setCourseType(e.target.value)} className="w-full bg-white border border-[#E9E2D2] rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D5C63]">
              <option value="">Select…</option>
              {courseTypeOptions.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </label>
          <label className={`block ${isInstituteLevel ? "opacity-60" : ""}`}>
            <div className="trust-eyebrow text-[10px] text-[#5B5B5B] font-bold mb-1.5 flex items-center justify-between">
              <span>Category</span>
              {isInstituteLevel && <span className="text-[9px] normal-case tracking-normal text-[#F26419] font-semibold">N/A</span>}
            </div>
            <select data-testid="trust-fees-category" value={isInstituteLevel ? "" : category} onChange={(e) => setCategory(e.target.value)} disabled={isInstituteLevel} className="w-full bg-white border border-[#E9E2D2] rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D5C63] disabled:bg-[#FAFAF7]">
              <option value="">{isInstituteLevel ? "— not applicable —" : "Select…"}</option>
              {categoryOptions.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </label>
        </div>

        <div className="mt-8">
          {!(admProc && courseType) || (!isInstituteLevel && !category) ? (
            <div className="bg-white border-2 border-dashed border-[#E9E2D2] rounded-2xl p-10 text-center" data-testid="trust-fees-empty">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-[#F26419]/15 to-[#D90368]/15 text-[#0D5C63]">
                <Sparkles size={22} />
              </div>
              <div className="trust-serif text-lg text-[#1A1A1A] mt-4">Pick the required filters to view fees</div>
              <p className="text-sm text-[#5B5B5B] mt-1">Choose Admission Process and Course Type. If Admission Process isn&apos;t <em>Institute Level</em>, also pick a Category.</p>
            </div>
          ) : loading ? (
            <div className="text-sm text-[#5B5B5B] flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading fee structure…</div>
          ) : !table.found || table.items.length === 0 ? (
            <div className="bg-white border border-[#E9E2D2] rounded-2xl p-10 text-center" data-testid="trust-fees-not-published">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#D90368]/10 text-[#D90368]">
                <Newspaper size={22} />
              </div>
              <div className="trust-serif text-lg text-[#1A1A1A] mt-4">Fee structure not published for this combination yet.</div>
              <p className="text-sm text-[#5B5B5B] mt-2">Please try a different combination or contact the institute office.</p>
              {status === "not_published" && <div className="mt-1 text-[11px] text-[#8a8478]">Combination: {admProc} · {courseType}{!isInstituteLevel && ` · ${category}`}</div>}
            </div>
          ) : (
            <div className="bg-white border border-[#E9E2D2] rounded-2xl overflow-hidden" data-testid="trust-fees-table-card">
              <div className="px-5 py-3.5 bg-[#FAFAF7] border-b border-[#E9E2D2]">
                <div className="trust-eyebrow text-[11px] tracking-[0.2em] uppercase text-[#5B5B5B] font-semibold">Fee Schedule</div>
                <div className="text-sm text-[#1A1A1A] font-semibold mt-0.5">
                  {activeInst?.name} · {ay} · <span className="text-[#0D5C63]">{courseType}</span> · <span className="text-[#F26419]">{admProc}</span>
                  {!isInstituteLevel && (<> · <span className="text-[#D90368]">{category}</span></>)}
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="trust-fees-table">
                  <thead className="bg-white border-b border-[#E9E2D2] text-[10px] tracking-[0.2em] uppercase text-[#5B5B5B]">
                    <tr>
                      <th className="w-14 text-left px-5 py-3 font-semibold">#</th>
                      <th className="text-left px-5 py-3 font-semibold">Fee Head</th>
                      <th className="w-56 text-right px-5 py-3 font-semibold">Amount (₹)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E9E2D2]">
                    {table.items.map((it, i) => (
                      <tr key={`${it.fee_header}-${i}`} data-testid={`trust-fees-row-${i}`} className="hover:bg-[#FAFAF7]/60">
                        <td className="px-5 py-2.5 text-[#8a8478] text-xs font-mono">{String(i + 1).padStart(2, "0")}</td>
                        <td className="px-5 py-2.5 text-[#1A1A1A]">{it.fee_header}</td>
                        <td className="px-5 py-2.5 text-right font-mono tabular-nums text-[#1A1A1A]">₹ {feesFormatINR(it.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gradient-to-r from-[#0D5C63] to-[#083E43] text-white" data-testid="trust-fees-total-row">
                      <td className="px-5 py-3" />
                      <td className="px-5 py-3 text-[11px] tracking-[0.2em] uppercase font-semibold">Grand Total</td>
                      <td className="px-5 py-3 text-right font-mono text-lg font-bold tabular-nums" data-testid="trust-fees-grand-total">
                        ₹ {feesFormatINR(table.total)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <div className="px-5 py-3 bg-[#FAFAF7] border-t border-[#E9E2D2] text-[11px] text-[#5B5B5B]">
                * Amounts are indicative and may be revised as per statutory / regulatory notifications. Please confirm with the institute office before making payment.
              </div>
            </div>
          )}
        </div>
        </>)}
      </section>
    </div>
  );
}


// ============================================================
// CONTACT
// ============================================================
export function TrustContact() {
  const data = useTrust();
  const t = data.trust || {};
  // Contact from Registered Office tab
  const corrAddr = [t.reg_address || t.corr_address, t.reg_city || t.corr_city, t.reg_state || t.corr_state, t.reg_pincode || t.corr_pincode].filter(Boolean).join(", ");
  const corrEmail = t.reg_email || t.corr_email || "";
  const corrPhone = t.reg_contact || t.corr_contact || "";
  const altPhone = t.reg_alt_contact || "";

  return (
    <div data-testid="trust-contact">
      <PageHero eyebrow="Reach Us" title="Get in Touch" sub="Visit the trust office or write to us — we love hearing from alumni, partners and well-wishers." />
      <section className="max-w-6xl mx-auto px-5 py-16 grid md:grid-cols-2 gap-12">
        <div>
          <div className="trust-eyebrow text-[10px] text-[#D90368] font-bold">Correspondence Office</div>
          <h2 className="trust-serif text-3xl text-[#1A1A1A] mt-2">{t.trust_name || "Gramaudyogik Shikshan Mandal"}</h2>
          <div className="gold-rule-solid mt-3 mb-6" />
          <div className="space-y-5">
            {corrAddr && (
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 bg-gradient-to-br from-[#F26419] to-[#D90368] text-white rounded-xl flex items-center justify-center shrink-0 shadow-md shadow-[#F26419]/20"><MapPin size={16} /></div>
                <div>
                  <div className="trust-eyebrow text-[10px] text-[#5B5B5B] font-bold">Address</div>
                  <div className="text-[#1A1A1A] mt-1">{corrAddr}</div>
                </div>
              </div>
            )}
            {corrEmail && (
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 bg-gradient-to-br from-[#0D5C63] to-[#083E43] text-white rounded-xl flex items-center justify-center shrink-0 shadow-md shadow-[#0D5C63]/20"><Mail size={16} /></div>
                <div>
                  <div className="trust-eyebrow text-[10px] text-[#5B5B5B] font-bold">Email</div>
                  <a href={`mailto:${corrEmail}`} className="text-[#1A1A1A] hover:text-[#F26419] block mt-1 transition-colors">{corrEmail}</a>
                </div>
              </div>
            )}
            {(corrPhone || altPhone) && (
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 bg-gradient-to-br from-[#D90368] to-[#F26419] text-white rounded-xl flex items-center justify-center shrink-0 shadow-md shadow-[#D90368]/20"><Phone size={16} /></div>
                <div>
                  <div className="trust-eyebrow text-[10px] text-[#5B5B5B] font-bold">Phone</div>
                  <div className="text-[#1A1A1A] mt-1">{[corrPhone, altPhone].filter(Boolean).join(" / ")}</div>
                </div>
              </div>
            )}
            {t.website && (
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 bg-gradient-to-br from-[#0D5C63] to-[#F26419] text-white rounded-xl flex items-center justify-center shrink-0 shadow-md"><ExternalLink size={15} /></div>
                <div>
                  <div className="trust-eyebrow text-[10px] text-[#5B5B5B] font-bold">Website</div>
                  <a href={t.website} target="_blank" rel="noopener noreferrer" className="text-[#1A1A1A] hover:text-[#F26419] block mt-1 transition-colors">{t.website}</a>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="bg-gradient-to-br from-[#0D5C63] via-[#083E43] to-[#0D5C63] text-white p-10 rounded-3xl relative overflow-hidden shadow-2xl">
          <div className="absolute -top-16 -right-16 w-64 h-64 bg-[#F26419]/20 rounded-full blur-2xl" />
          <div className="absolute -bottom-16 -left-16 w-64 h-64 bg-[#D90368]/20 rounded-full blur-2xl" />
          <div className="relative">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white/15 backdrop-blur">
              <Quote size={26} className="text-[#F26419]" />
            </div>
            <h3 className="trust-serif text-3xl mt-5 leading-tight">Reach out to the <span className="grad-text">Trust Secretariat</span>.</h3>
            <p className="mt-4 text-white/85">For alumni records, donor partnerships, academic affiliations, recruitment or media enquiries — we&apos;d be glad to help direct you to the right person.</p>
            {corrEmail && (
              <a href={`mailto:${corrEmail}`} className="mt-8 inline-flex items-center gap-2 bg-gradient-to-r from-[#F26419] to-[#D90368] hover:from-[#D14E10] hover:to-[#B00252] text-white font-bold text-sm uppercase tracking-wider px-6 py-3 rounded-full shadow-lg hover:-translate-y-0.5 transition-all" data-testid="trust-contact-email-cta">
                <Mail size={14} /> Write to us
              </a>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

// ============================================================
// Shared
// ============================================================
function PageHero({ eyebrow, title, sub }) {
  return (
    <section className="bg-gradient-to-br from-[#0D5C63] via-[#0D5C63] to-[#083E43] text-white py-20 relative overflow-hidden">
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#F26419]/15 blur-3xl" />
      <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-[#D90368]/15 blur-3xl" />
      <div className="absolute top-6 right-6 grid grid-cols-6 gap-2 opacity-30" aria-hidden>
        {Array.from({ length: 24 }).map((_, i) => <span key={i} className="w-1.5 h-1.5 rounded-full bg-white" />)}
      </div>
      <div className="relative max-w-7xl mx-auto px-5">
        <div className="trust-eyebrow text-[10px] text-[#F26419] font-bold trust-rise trust-rise-1">{eyebrow}</div>
        <h1 className="trust-serif text-5xl sm:text-6xl mt-3 leading-tight trust-rise trust-rise-2">{title}</h1>
        <div className="gold-rule-solid mt-4 trust-rise trust-rise-3" />
        {sub && <p className="mt-5 max-w-2xl text-white/85 trust-rise trust-rise-3">{sub}</p>}
      </div>
    </section>
  );
}

function EmptyState({ icon: Icon, msg }) {
  return (
    <div className="bg-white border border-[#E9E2D2] rounded-2xl p-14 text-center">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-[#F26419]/15 to-[#D90368]/15 text-[#0D5C63]">
        <Icon size={28} strokeWidth={1.5} />
      </div>
      <p className="trust-serif text-xl text-[#1A1A1A] mt-4">{msg}</p>
    </div>
  );
}

// ============================================================
// CAREERS
// ============================================================
export function TrustCareers() {
  const [jobs, setJobs] = useState([]);
  const [institutes, setInstitutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applyFor, setApplyFor] = useState(null);
  const [generalOpen, setGeneralOpen] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get("/public/trust/jobs");
        if (!cancel) { setJobs(data.jobs || []); setInstitutes(byYear(data.institutes || [])); }
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, []);

  return (
    <div data-testid="trust-careers">
      <section className="bg-gradient-to-br from-[#0D5C63] via-[#083E43] to-[#0D5C63] text-white py-20 relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#F26419]/15 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-[#D90368]/15 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 flex flex-wrap items-end justify-between gap-6">
          <div className="min-w-0 trust-rise trust-rise-1">
            <div className="trust-eyebrow text-[10px] text-[#F26419] font-bold">Careers</div>
            <h1 className="trust-serif text-5xl sm:text-6xl mt-3 leading-tight">Build with <span className="grad-text">us</span></h1>
            <div className="gold-rule-solid mt-4" />
            <p className="mt-5 max-w-2xl text-white/85">Openings across the GS Mandal family of institutes. Don&apos;t see a role that fits? Send us your resume — we&apos;ll reach out when a matching position opens up.</p>
          </div>
          <button
            type="button"
            onClick={() => setGeneralOpen(true)}
            data-testid="trust-careers-general-btn"
            className="group inline-flex items-center gap-2 bg-gradient-to-r from-[#F26419] to-[#D90368] hover:from-[#D14E10] hover:to-[#B00252] text-white font-bold text-xs uppercase tracking-wider px-6 py-3.5 rounded-full shadow-lg shadow-[#F26419]/30 hover:-translate-y-0.5 transition-all"
          >
            <Upload size={14} /> Send Your Resume
          </button>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 py-14">
        {loading ? (
          <div className="text-sm text-[#5B5B5B]">Loading…</div>
        ) : jobs.length === 0 ? (
          <div className="bg-white border border-[#E9E2D2] rounded-2xl p-14 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-[#F26419]/15 to-[#D90368]/15 text-[#0D5C63]">
              <Briefcase size={28} strokeWidth={1.5} />
            </div>
            <div className="trust-serif text-xl text-[#1A1A1A] mt-4">No open positions right now</div>
            <p className="mt-2 text-sm text-[#3E3E3E] max-w-lg mx-auto">Send us your resume and we&apos;ll keep it on file — we&apos;ll get in touch when a matching role opens across any of our institutes.</p>
            <button type="button" onClick={() => setGeneralOpen(true)} className="mt-6 inline-flex items-center gap-2 bg-[#0D5C63] hover:bg-[#083E43] text-white font-bold text-xs uppercase tracking-wider px-6 py-3 rounded-full">
              <Upload size={14} /> Send Your Resume
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {jobs.map((j) => (
              <article key={j.id} data-testid={`trust-careers-job-${j.id}`} className="group bg-white border border-[#E9E2D2] rounded-2xl p-6 hover:border-[#F26419]/40 trust-card-hover">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="trust-serif text-xl text-[#1A1A1A] group-hover:text-[#0D5C63] transition-colors">{j.title}</h3>
                      <span className="trust-eyebrow text-[10px] px-2.5 py-0.5 rounded-full font-bold bg-[#0D5C63]/10 text-[#0D5C63]">{j.employment_type}</span>
                    </div>
                    <div className="mt-1.5 trust-eyebrow text-[10px] font-bold text-[#D90368]">{j.institute_name}</div>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-[#3E3E3E]">
                      {j.location && <span className="inline-flex items-center gap-1"><MapPin size={11} className="text-[#F26419]" />{j.location}</span>}
                      {j.salary_range && <span>{j.salary_range}</span>}
                      <span>{j.openings} opening{j.openings > 1 ? "s" : ""}</span>
                      {j.deadline && <span>Apply by {fmtDate(j.deadline)}</span>}
                    </div>
                    {j.qualification && <div className="mt-3 text-sm text-[#1A1A1A]"><span className="font-semibold">Qualification:</span> {j.qualification}</div>}
                    {j.description && <div className="mt-2 text-sm text-[#3E3E3E] whitespace-pre-line">{j.description}</div>}
                  </div>
                  <button type="button" onClick={() => setApplyFor(j)} data-testid={`trust-careers-apply-${j.id}`} className="group inline-flex items-center gap-2 bg-gradient-to-r from-[#F26419] to-[#D90368] hover:from-[#D14E10] hover:to-[#B00252] text-white font-semibold text-xs uppercase tracking-wider px-5 py-2.5 rounded-full shadow-md hover:-translate-y-0.5 transition-all">
                    Apply Now <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {applyFor && <TrustApplyModal job={applyFor} institutes={institutes} onClose={() => setApplyFor(null)} />}
      {generalOpen && <TrustApplyModal general institutes={institutes} onClose={() => setGeneralOpen(false)} />}
    </div>
  );
}

const CAREERS_SAFE_FILENAME = /^[A-Za-z0-9._ \-]+$/;

function TrustApplyModal({ job, general = false, institutes = [], onClose }) {
  const [form, setForm] = useState({
    institute_id: job?.institute_id || (institutes[0]?.id || ""),
    full_name: "", mobile: "", email: "", years_experience: "0", current_profession: "",
  });
  const [resume, setResume] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) { setResume(null); return; }
    if (f.type !== "application/pdf") { toast.error("Resume must be a PDF file"); e.target.value = ""; return; }
    if (f.size > 500 * 1024) { toast.error("Resume must be 500 KB or smaller"); e.target.value = ""; return; }
    const stem = (f.name || "").replace(/\.pdf$/i, "");
    if (!CAREERS_SAFE_FILENAME.test(stem)) { toast.error("Filename must only contain letters, numbers, spaces, dots, hyphens and underscores."); e.target.value = ""; return; }
    setResume(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.institute_id) return toast.error("Please choose the institute");
    if (!form.full_name.trim()) return toast.error("Please enter your full name");
    const m = form.mobile.replace(/\D/g, "");
    if (m.length !== 10) return toast.error("Mobile must be a 10-digit number");
    if (form.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) return toast.error("Invalid email");
    if (!resume) return toast.error("Please attach your resume (PDF, 500 KB max)");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("full_name", form.full_name.trim());
      fd.append("mobile", m);
      fd.append("email", form.email.trim());
      fd.append("years_experience", String(form.years_experience || 0));
      fd.append("current_profession", form.current_profession.trim());
      fd.append("resume", resume, resume.name);
      let url;
      if (general) {
        fd.append("institute_id", form.institute_id);
        url = "/public/trust/apply-general";
      } else {
        const inst = institutes.find((i) => i.id === job.institute_id) || {};
        url = `/public/institutes/${inst.website_slug}/jobs/${job.id}/apply`;
      }
      await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(general ? "Resume received. We'll be in touch when a matching role opens." : "Application submitted. Thank you.");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally { setBusy(false); }
  };

  const inputCls = "w-full border border-[#E9E2D2] bg-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#F26419]/40 focus:border-[#F26419] transition-colors";

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" role="dialog" data-testid={general ? "trust-careers-general-modal" : "trust-careers-apply-modal"}>
      <div className="bg-white w-full max-w-2xl mt-6 mb-16 rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-start justify-between gap-4 border-b border-[#E9E2D2] bg-gradient-to-br from-[#FAFAF7] to-white px-6 py-5">
          <div>
            <div className="trust-eyebrow text-[10px] text-[#D90368] font-bold">{general ? "General Application · Talent Pool" : "Apply for"}</div>
            <h3 className="trust-serif text-2xl text-[#1A1A1A] mt-0.5">{general ? "Send us your resume" : job.title}</h3>
            {general && <div className="mt-1 text-xs text-[#3E3E3E]">Pick an institute and we&apos;ll reach out when a matching role opens.</div>}
            {!general && <div className="mt-1 text-xs text-[#F26419] font-semibold">{institutes.find((i) => i.id === job.institute_id)?.name || ""}</div>}
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="text-[#3E3E3E] hover:text-[#D90368] transition-colors"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="px-6 py-5 grid sm:grid-cols-2 gap-4">
          {general && (
            <label className="block sm:col-span-2">
              <div className="trust-eyebrow text-[10px] text-[#0D5C63] font-bold mb-1">Institute *</div>
              <select data-testid="trust-careers-apply-institute" className={inputCls} value={form.institute_id} onChange={(e) => set("institute_id", e.target.value)}>
                <option value="">— Choose institute —</option>
                {institutes.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
              </select>
            </label>
          )}
          <label className="block sm:col-span-2">
            <div className="trust-eyebrow text-[10px] text-[#0D5C63] font-bold mb-1">Full Name *</div>
            <input data-testid="trust-careers-apply-name" type="text" maxLength={160} className={inputCls} value={form.full_name} onChange={(e) => set("full_name", e.target.value)} required />
          </label>
          <label className="block">
            <div className="trust-eyebrow text-[10px] text-[#0D5C63] font-bold mb-1">Contact No. *</div>
            <input data-testid="trust-careers-apply-mobile" type="tel" inputMode="numeric" pattern="[0-9]{10}" maxLength={10} className={inputCls} value={form.mobile} onChange={(e) => set("mobile", e.target.value.replace(/\D/g, ""))} placeholder="10-digit" required />
          </label>
          <label className="block">
            <div className="trust-eyebrow text-[10px] text-[#0D5C63] font-bold mb-1">Email ID</div>
            <input data-testid="trust-careers-apply-email" type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="optional" />
          </label>
          <label className="block">
            <div className="trust-eyebrow text-[10px] text-[#0D5C63] font-bold mb-1">Years of Experience *</div>
            <select data-testid="trust-careers-apply-yoe" className={inputCls} value={form.years_experience} onChange={(e) => set("years_experience", e.target.value)}>
              {Array.from({ length: 51 }).map((_, i) => <option key={i} value={i}>{i} {i === 0 ? "(Fresher)" : (i === 1 ? "year" : "years")}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="trust-eyebrow text-[10px] text-[#0D5C63] font-bold mb-1">Resume (PDF, 500 KB max) *</div>
            <input data-testid="trust-careers-apply-resume" type="file" accept="application/pdf" onChange={pickFile} className="block w-full text-xs file:mr-3 file:py-2 file:px-3 file:rounded-sm file:border-0 file:bg-[#0D5C63] file:text-white file:font-semibold hover:file:bg-[#083E43]" required />
            {resume && <div className="mt-1 text-[11px] text-[#3E3E3E]">{resume.name} · {(resume.size / 1024).toFixed(0)} KB</div>}
            <div className="mt-1 text-[10px] text-[#8b8578]">Filename can only contain letters, numbers, spaces, dots, hyphens and underscores.</div>
          </label>
          <label className="block sm:col-span-2">
            <div className="trust-eyebrow text-[10px] text-[#0D5C63] font-bold mb-1">Current Profession</div>
            <textarea data-testid="trust-careers-apply-profession" rows={4} maxLength={2000} className={`${inputCls} resize-y`} value={form.current_profession} onChange={(e) => set("current_profession", e.target.value)} placeholder="Briefly describe your current role, employer, and any relevant experience (2000 characters max)" />
            <div className="mt-1 text-[10px] text-[#8b8578] text-right">{form.current_profession.length}/2000</div>
          </label>
          <div className="sm:col-span-2 flex items-center justify-end gap-3">
            <button type="button" onClick={onClose} className="text-sm px-4 py-2.5 rounded-sm border border-[#E9E2D2] text-[#3E3E3E] hover:bg-[#f5f1e8]">Cancel</button>
            <button type="submit" disabled={busy} data-testid="trust-careers-apply-submit" className="inline-flex items-center gap-2 bg-gradient-to-r from-[#F26419] to-[#D90368] hover:from-[#D14E10] hover:to-[#B00252] disabled:opacity-60 text-white font-bold text-sm uppercase tracking-wider px-6 py-2.5 rounded-full shadow-md hover:-translate-y-0.5 transition-all">
              {busy ? (<><Loader2 size={14} className="animate-spin" /> Submitting…</>) : (<><Upload size={14} /> Submit Application</>)}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


// ============================================================
// SOCIAL IMPACT (public)
// ============================================================
function toEmbedUrl(link) {
  if (!link) return "";
  try {
    const u = new URL(link);
    const host = u.hostname.replace(/^www\./, "");
    if (host === "youtube.com" || host === "m.youtube.com") {
      const id = u.searchParams.get("v");
      if (id) return `https://www.youtube.com/embed/${id}`;
    }
    if (host === "youtu.be") {
      const id = u.pathname.replace(/^\//, "").split("/")[0];
      if (id) return `https://www.youtube.com/embed/${id}`;
    }
    if (host === "vimeo.com") {
      const id = u.pathname.replace(/^\//, "").split("/")[0];
      if (id && /^\d+$/.test(id)) return `https://player.vimeo.com/video/${id}`;
    }
  } catch (_) { /* fall through */ }
  return "";
}

function SocialImpactStory({ item, idx, index }) {
  const [lbIdx, setLbIdx] = useState(-1);
  const photos = Array.isArray(item.photos) ? item.photos.filter((p) => p && p.url) : [];
  const embed = toEmbedUrl(item.video_link);
  const flip = idx % 2 === 1;
  return (
    <article id={`story-${item.id}`} className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start scroll-mt-24" data-testid={`trust-si-story-${item.id}`}>
      <div className={`${flip ? "lg:order-2" : ""} space-y-4`}>
        <div className="flex items-center gap-3">
          <div className="trust-eyebrow text-[10px] text-[#F26419] font-bold">Story {String(index + 1).padStart(2, "0")} · Social Impact</div>
          <a
            href="#si-index"
            onClick={(e) => {
              const el = document.getElementById("si-index");
              if (el) {
                e.preventDefault();
                el.scrollIntoView({ behavior: "smooth", block: "start" });
                window.history.replaceState(null, "", "#si-index");
              }
            }}
            className="text-[10px] uppercase tracking-widest text-[#8a8478] hover:text-[#F26419] font-semibold"
            data-testid={`trust-si-back-to-index-${item.id}`}
          >↑ Back to index</a>
        </div>
        <h2 className="trust-serif text-3xl md:text-4xl leading-tight text-[#1A1A1A]" data-testid={`trust-si-title-${item.id}`}>{item.title}</h2>
        {item.story && (
          <div
            className="prose prose-sm max-w-none text-[15px] leading-relaxed text-[#3E3E3E] trust-si-prose"
            data-testid={`trust-si-story-body-${item.id}`}
            dangerouslySetInnerHTML={{ __html: item.story }}
          />
        )}
        {item.video_link && !embed && (
          <a href={item.video_link} target="_blank" rel="noopener noreferrer" data-testid={`trust-si-video-link-${item.id}`} className="inline-flex items-center gap-2 text-[#F26419] hover:text-[#D90368] text-sm font-semibold">
            <PlayCircle size={16} /> Watch video
          </a>
        )}
      </div>
      <div className={`${flip ? "lg:order-1" : ""} space-y-3`}>
        {embed && (
          <div className="aspect-video w-full overflow-hidden rounded-xl border border-[#E9E2D2] shadow-sm bg-black" data-testid={`trust-si-embed-${item.id}`}>
            <iframe
              src={embed}
              title={item.title}
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
            />
          </div>
        )}
        {photos.length > 0 && (
          <div className="grid grid-cols-3 gap-2" data-testid={`trust-si-photos-${item.id}`}>
            {photos.map((p, i) => (
              <button
                type="button"
                key={i}
                onClick={() => setLbIdx(i)}
                className={`relative bg-[#0D5C63]/5 rounded-lg overflow-hidden aspect-square group ${i === 0 && photos.length > 1 ? "col-span-3 aspect-video" : ""}`}
                data-testid={`trust-si-photo-${item.id}-${i}`}
              >
                <img src={p.url} alt={`${item.title} ${i + 1}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </button>
            ))}
          </div>
        )}
      </div>

      {lbIdx >= 0 && photos[lbIdx] && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4" onClick={() => setLbIdx(-1)} data-testid={`trust-si-lightbox-${item.id}`}>
          <button type="button" onClick={(e) => { e.stopPropagation(); setLbIdx(-1); }} className="absolute top-5 right-5 text-white hover:text-[#F26419] p-2" aria-label="Close" data-testid="trust-si-lightbox-close">
            <X size={24} />
          </button>
          {photos.length > 1 && (
            <>
              <button type="button" onClick={(e) => { e.stopPropagation(); setLbIdx((i) => (i - 1 + photos.length) % photos.length); }} className="absolute left-5 text-white hover:text-[#F26419] p-3" aria-label="Previous" data-testid="trust-si-lightbox-prev">
                <ArrowLeft size={24} />
              </button>
              <button type="button" onClick={(e) => { e.stopPropagation(); setLbIdx((i) => (i + 1) % photos.length); }} className="absolute right-5 text-white hover:text-[#F26419] p-3" aria-label="Next" data-testid="trust-si-lightbox-next">
                <ArrowRight size={24} />
              </button>
            </>
          )}
          <img src={photos[lbIdx].url} alt={`${item.title} ${lbIdx + 1}`} className="max-h-[85vh] max-w-[85vw] object-contain" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </article>
  );
}

export function TrustSocialImpact() {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get("/public/social-impact");
        if (!cancel) setItems(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancel) setErr(e?.response?.data?.detail || "Unable to load Social Impact stories");
      }
    })();
    return () => { cancel = true; };
  }, []);

  // When the list finishes loading and a `#story-XXX` hash is already in the URL,
  // scroll smoothly to the target — the anchor may have been ignored during the
  // initial paint because the element didn't yet exist.
  useEffect(() => {
    if (!items || items.length === 0) return;
    const hash = window.location.hash;
    if (!hash || !hash.startsWith("#story-")) return;
    const el = document.getElementById(hash.slice(1));
    if (el) {
      requestAnimationFrame(() => el.scrollIntoView({ behavior: "smooth", block: "start" }));
    }
  }, [items]);

  return (
    <div data-testid="trust-social-impact-page">
      <section className="bg-gradient-to-br from-[#0D5C63] via-[#083E43] to-[#0D5C63] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 trust-grain" />
        <div className="max-w-7xl mx-auto px-5 py-16 md:py-20 relative">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1">
            <HeartHandshake size={14} className="text-[#F26419]" />
            <span className="trust-eyebrow text-[10px] font-bold text-white">Community &middot; Outreach &middot; Change</span>
          </div>
          <h1 className="trust-serif text-4xl md:text-5xl lg:text-6xl mt-4 leading-tight max-w-3xl">Social <span className="text-[#F26419]">Impact</span></h1>
          <p className="mt-4 text-white/85 max-w-2xl text-[15px] leading-relaxed">
            Stories from the field &mdash; how the GS Mandal family is contributing beyond the classroom through outreach, service, awareness and social good.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 py-14 space-y-16">
        {err ? (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-4" data-testid="trust-si-error">{err}</div>
        ) : items === null ? (
          <div className="flex items-center gap-3 text-sm text-[#5B5B5B]" data-testid="trust-si-loading"><Loader2 size={16} className="animate-spin text-[#F26419]" /> Loading stories&hellip;</div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-[#E9E2D2] rounded-2xl p-10 text-center" data-testid="trust-si-empty">
            <HeartHandshake size={36} className="mx-auto text-[#0D5C63]" strokeWidth={1.4} />
            <div className="mt-3 trust-serif text-xl text-[#1A1A1A]">Coming soon</div>
            <p className="mt-2 text-sm text-[#5B5B5B] max-w-md mx-auto">We&rsquo;re curating our social impact stories. Please check back shortly.</p>
          </div>
        ) : (
          <>
            {/* Titles index — bookmarked links to each full story below */}
            <nav id="si-index" className="bg-white border border-[#E9E2D2] rounded-2xl px-6 py-6 md:px-8 md:py-7 scroll-mt-24" data-testid="trust-si-index" aria-label="Stories index">
              <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                <div>
                  <div className="trust-eyebrow text-[10px] text-[#F26419] font-bold">In this section</div>
                  <div className="trust-serif text-2xl md:text-3xl text-[#1A1A1A] mt-0.5">{items.length} {items.length === 1 ? "story" : "stories"}</div>
                </div>
                <div className="text-xs text-[#5B5B5B]">Tap any title to jump to the full story.</div>
              </div>
              <ol className="space-y-1 columns-1 md:columns-2 gap-x-8">
                {items.map((it, i) => (
                  <li key={it.id} className="break-inside-avoid" data-testid={`trust-si-index-item-${it.id}`}>
                    <a
                      href={`#story-${it.id}`}
                      data-testid={`trust-si-index-link-${it.id}`}
                      onClick={(e) => {
                        const el = document.getElementById(`story-${it.id}`);
                        if (el) {
                          e.preventDefault();
                          el.scrollIntoView({ behavior: "smooth", block: "start" });
                          window.history.replaceState(null, "", `#story-${it.id}`);
                        }
                      }}
                      className="group flex items-baseline gap-3 py-2 border-b border-[#F1EBDD] hover:bg-[#FCF7EE] rounded-sm px-2 -mx-2 transition-colors"
                    >
                      <span className="font-mono text-[10px] tracking-widest text-[#F26419] font-bold w-6 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                      <span className="trust-serif text-base md:text-lg text-[#1A1A1A] group-hover:text-[#0D5C63] leading-snug flex-1">{it.title}</span>
                      <span className="text-[#8a8478] group-hover:text-[#0D5C63] text-sm shrink-0" aria-hidden="true">↓</span>
                    </a>
                  </li>
                ))}
              </ol>
            </nav>

            {items.map((it, i) => <SocialImpactStory key={it.id} item={it} idx={i} index={i} />)}
          </>
        )}
      </section>
    </div>
  );
}


// ============================================================
// AMENITIES (public) — same design language as Social Impact
// ============================================================
function AmenityStory({ item, idx, index }) {
  const [lbIdx, setLbIdx] = useState(-1);
  const photos = Array.isArray(item.photos) ? item.photos.filter((p) => p && p.url) : [];
  const embed = toEmbedUrl(item.video_link);
  const flip = idx % 2 === 1;
  return (
    <article id={`amenity-${item.id}`} className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start scroll-mt-24" data-testid={`trust-amen-story-${item.id}`}>
      <div className={`${flip ? "lg:order-2" : ""} space-y-4`}>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="trust-eyebrow text-[10px] text-[#F26419] font-bold">Amenity {String(index + 1).padStart(2, "0")} · {item.amenity_type || "Facility"}</div>
          <a
            href="#amen-index"
            onClick={(e) => {
              const el = document.getElementById("amen-index");
              if (el) { e.preventDefault(); el.scrollIntoView({ behavior: "smooth", block: "start" }); window.history.replaceState(null, "", "#amen-index"); }
            }}
            className="text-[10px] uppercase tracking-widest text-[#8a8478] hover:text-[#F26419] font-semibold"
            data-testid={`trust-amen-back-${item.id}`}
          >↑ Back to index</a>
        </div>
        <h2 className="trust-serif text-3xl md:text-4xl leading-tight text-[#1A1A1A]" data-testid={`trust-amen-title-${item.id}`}>{item.title}</h2>
        {item.description && (
          <div
            className="prose prose-sm max-w-none text-[15px] leading-relaxed text-[#3E3E3E] trust-si-prose"
            data-testid={`trust-amen-desc-${item.id}`}
            dangerouslySetInnerHTML={{ __html: item.description }}
          />
        )}
        {item.video_link && !embed && (
          <a href={item.video_link} target="_blank" rel="noopener noreferrer" data-testid={`trust-amen-video-link-${item.id}`} className="inline-flex items-center gap-2 text-[#F26419] hover:text-[#D90368] text-sm font-semibold">
            <PlayCircle size={16} /> Watch video
          </a>
        )}
        {item.booking_enabled && (
          <div className="pt-2">
            <Link
              to={`/book-amenity/${item.id}`}
              data-testid={`trust-amen-book-${item.id}`}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-[#F26419] to-[#D90368] hover:from-[#D90368] hover:to-[#F26419] text-white text-xs font-bold uppercase tracking-wider px-5 py-2.5 rounded-full shadow-lg shadow-[#F26419]/25"
            >
              <Calendar size={14} /> Check availability &amp; Book
            </Link>
            <div className="mt-2 text-[11px] text-[#8a8478]" data-testid={`trust-amen-price-${item.id}`}>
              {["Hostel", "Guest House"].includes(item.amenity_type) ? (
                item.full_day_charge > 0 ? (
                  <>
                    <b className="text-[#0D5C63]">₹{Number(item.full_day_charge).toLocaleString("en-IN")}</b>
                    <span> per night</span>
                    {Number(item.tax_percent || 0) > 0 && <span className="italic ml-1">+ {Number(item.tax_percent)}% GST</span>}
                  </>
                ) : null
              ) : (
                <>
                  {item.half_day_charge > 0 && <>Half day <b className="text-[#0D5C63]">₹{Number(item.half_day_charge).toLocaleString("en-IN")}</b></>}
                  {item.half_day_charge > 0 && item.full_day_charge > 0 && <span className="mx-1">&middot;</span>}
                  {item.full_day_charge > 0 && <>Full day <b className="text-[#0D5C63]">₹{Number(item.full_day_charge).toLocaleString("en-IN")}</b></>}
                  {(item.half_day_charge > 0 || item.full_day_charge > 0) && Number(item.tax_percent || 0) > 0 && (
                    <span className="italic ml-1">+ {Number(item.tax_percent)}% GST</span>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
      <div className={`${flip ? "lg:order-1" : ""} space-y-3`}>
        {embed && (
          <div className="aspect-video w-full overflow-hidden rounded-xl border border-[#E9E2D2] shadow-sm bg-black" data-testid={`trust-amen-embed-${item.id}`}>
            <iframe
              src={embed}
              title={item.title}
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
            />
          </div>
        )}
        {photos.length > 0 && (
          <div className="grid grid-cols-3 gap-2" data-testid={`trust-amen-photos-${item.id}`}>
            {photos.map((p, i) => (
              <button
                type="button"
                key={i}
                onClick={() => setLbIdx(i)}
                className={`relative bg-[#0D5C63]/5 rounded-lg overflow-hidden aspect-square group ${i === 0 && photos.length > 1 ? "col-span-3 aspect-video" : ""}`}
                data-testid={`trust-amen-photo-${item.id}-${i}`}
              >
                <img src={p.url} alt={`${item.title} ${i + 1}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </button>
            ))}
          </div>
        )}
      </div>

      {lbIdx >= 0 && photos[lbIdx] && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4" onClick={() => setLbIdx(-1)} data-testid={`trust-amen-lightbox-${item.id}`}>
          <button type="button" onClick={(e) => { e.stopPropagation(); setLbIdx(-1); }} className="absolute top-5 right-5 text-white hover:text-[#F26419] p-2" aria-label="Close" data-testid="trust-amen-lightbox-close">
            <X size={24} />
          </button>
          {photos.length > 1 && (
            <>
              <button type="button" onClick={(e) => { e.stopPropagation(); setLbIdx((i) => (i - 1 + photos.length) % photos.length); }} className="absolute left-5 text-white hover:text-[#F26419] p-3" aria-label="Previous" data-testid="trust-amen-lightbox-prev">
                <ArrowLeft size={24} />
              </button>
              <button type="button" onClick={(e) => { e.stopPropagation(); setLbIdx((i) => (i + 1) % photos.length); }} className="absolute right-5 text-white hover:text-[#F26419] p-3" aria-label="Next" data-testid="trust-amen-lightbox-next">
                <ArrowRight size={24} />
              </button>
            </>
          )}
          <img src={photos[lbIdx].url} alt={`${item.title} ${lbIdx + 1}`} className="max-h-[85vh] max-w-[85vw] object-contain" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </article>
  );
}

export function TrustAmenities() {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get("/public/amenities");
        if (!cancel) setItems(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancel) setErr(e?.response?.data?.detail || "Unable to load Amenities");
      }
    })();
    return () => { cancel = true; };
  }, []);

  useEffect(() => {
    if (!items || items.length === 0) return;
    const hash = window.location.hash;
    if (!hash || !hash.startsWith("#amenity-")) return;
    const el = document.getElementById(hash.slice(1));
    if (el) requestAnimationFrame(() => el.scrollIntoView({ behavior: "smooth", block: "start" }));
  }, [items]);

  const types = items ? Array.from(new Set(items.map((i) => i.amenity_type).filter(Boolean))).sort() : [];
  const filtered = items && typeFilter ? items.filter((i) => i.amenity_type === typeFilter) : items;

  return (
    <div data-testid="trust-amenities-page">
      <section className="bg-gradient-to-br from-[#0D5C63] via-[#083E43] to-[#0D5C63] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 trust-grain" />
        <div className="max-w-7xl mx-auto px-5 py-16 md:py-20 relative">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1">
            <Sparkles size={14} className="text-[#F26419]" />
            <span className="trust-eyebrow text-[10px] font-bold text-white">Facilities &middot; Comfort &middot; Support</span>
          </div>
          <h1 className="trust-serif text-4xl md:text-5xl lg:text-6xl mt-4 leading-tight max-w-3xl"><span className="text-[#F26419]">Amenities</span> at GS Mandal</h1>
          <p className="mt-4 text-white/85 max-w-2xl text-[15px] leading-relaxed">
            Everything the GS Mandal family provides beyond the classroom &mdash; libraries, hostels, sports, transport, medical support and more.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 py-14 space-y-16">
        {err ? (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-4" data-testid="trust-amen-error">{err}</div>
        ) : items === null ? (
          <div className="flex items-center gap-3 text-sm text-[#5B5B5B]" data-testid="trust-amen-loading"><Loader2 size={16} className="animate-spin text-[#F26419]" /> Loading amenities&hellip;</div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-[#E9E2D2] rounded-2xl p-10 text-center" data-testid="trust-amen-empty">
            <Sparkles size={36} className="mx-auto text-[#0D5C63]" strokeWidth={1.4} />
            <div className="mt-3 trust-serif text-xl text-[#1A1A1A]">Coming soon</div>
            <p className="mt-2 text-sm text-[#5B5B5B] max-w-md mx-auto">We&rsquo;re curating our facilities. Please check back shortly.</p>
          </div>
        ) : (
          <>
            <nav id="amen-index" className="bg-white border border-[#E9E2D2] rounded-2xl px-6 py-6 md:px-8 md:py-7 scroll-mt-24" data-testid="trust-amen-index" aria-label="Amenities index">
              <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                <div>
                  <div className="trust-eyebrow text-[10px] text-[#F26419] font-bold">On this page</div>
                  <div className="trust-serif text-2xl md:text-3xl text-[#1A1A1A] mt-0.5">{filtered.length} {filtered.length === 1 ? "amenity" : "amenities"}</div>
                </div>
                {types.length > 1 && (
                  <div className="flex items-center gap-2 flex-wrap" data-testid="trust-amen-type-filter">
                    <button type="button" onClick={() => setTypeFilter("")} className={`text-[10px] tracking-widest uppercase font-bold px-2.5 py-1 rounded-full border ${typeFilter === "" ? "bg-[#0D5C63] text-white border-[#0D5C63]" : "border-[#E9E2D2] text-[#5B5B5B] hover:border-[#0D5C63]"}`}>All</button>
                    {types.map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setTypeFilter(t)}
                        data-testid={`trust-amen-filter-${t}`}
                        className={`text-[10px] tracking-widest uppercase font-bold px-2.5 py-1 rounded-full border ${typeFilter === t ? "bg-[#0D5C63] text-white border-[#0D5C63]" : "border-[#E9E2D2] text-[#5B5B5B] hover:border-[#0D5C63]"}`}
                      >{t}</button>
                    ))}
                  </div>
                )}
              </div>
              {filtered.length === 0 ? (
                <div className="text-sm text-[#8a8478] text-center py-3">No amenities in this category.</div>
              ) : (
                <ol className="space-y-1 columns-1 md:columns-2 gap-x-8">
                  {filtered.map((it, i) => (
                    <li key={it.id} className="break-inside-avoid" data-testid={`trust-amen-index-item-${it.id}`}>
                      <a
                        href={`#amenity-${it.id}`}
                        data-testid={`trust-amen-index-link-${it.id}`}
                        onClick={(e) => {
                          const el = document.getElementById(`amenity-${it.id}`);
                          if (el) { e.preventDefault(); el.scrollIntoView({ behavior: "smooth", block: "start" }); window.history.replaceState(null, "", `#amenity-${it.id}`); }
                        }}
                        className="group flex items-baseline gap-3 py-2 border-b border-[#F1EBDD] hover:bg-[#FCF7EE] rounded-sm px-2 -mx-2 transition-colors"
                      >
                        <span className="font-mono text-[10px] tracking-widest text-[#F26419] font-bold w-6 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                        <span className="trust-serif text-base md:text-lg text-[#1A1A1A] group-hover:text-[#0D5C63] leading-snug flex-1">{it.title}</span>
                        <span className="text-[10px] tracking-widest uppercase text-[#8a8478] font-semibold shrink-0">{it.amenity_type}</span>
                        <span className="text-[#8a8478] group-hover:text-[#0D5C63] text-sm shrink-0" aria-hidden="true">↓</span>
                      </a>
                    </li>
                  ))}
                </ol>
              )}
            </nav>

            {filtered.map((it, i) => <AmenityStory key={it.id} item={it} idx={i} index={i} />)}
          </>
        )}
      </section>
    </div>
  );
}

